/**
 * PanelScheduleDesigner — /panel-schedule and /panel-schedule/:scheduleId.
 *
 * WHY NATIVE: a panel directory is the one document that is FINAL at the
 * customer's panel at the end of the job and lives on the door for 20 years.
 * Jobber has nowhere to hold it as data (a note is prose, a form can't be read
 * back), so ElecBid owns the record (panel_schedules, served only through
 * /api/panel-schedule) and mirrors OUT: a note + the printed card land on the
 * Jobber job, and the card itself prints to a Brother QL 62 mm film strip.
 *
 * Four rules shape this file:
 *   • phone-first (375–430 px): the tech edits at the panel, so every row is a
 *     40 px+ tap target and editing happens in a bottom sheet, never inline
 *   • the auto-size preview is LOCAL (§3 layout math copied here) so the strip
 *     grows in front of the tech as circuits go in — the server's resolveSize
 *     stays authoritative at export time; the preview is for feel
 *   • every keystroke lands in localStorage first (elecbid_panel_<id>) and the
 *     whole document saves 1.5 s after the last change; a save (or accept)
 *     RESPONSE never overwrites typing that happened while it was in flight,
 *     and nothing is exported or sent to Jobber past a save that failed
 *   • "needs review" is the SERVER's call: once its copy is the one on screen,
 *     row.needs_review decides; the local twin of the rule only previews edits
 *     the server has not seen yet
 *
 * Only HEAD rows are stored (a 2-pole at 1 occupies 1 and 3); continuation rows
 * are derived here for the grid and the preview, and again on the server.
 *
 * v1.1 (contract §11.5): read a panel PHOTO (the server resolves the bytes from
 * a key — the browser never hands it a URL), "uncertain" rows a human confirms
 * one by one against the pinned picture, a standard label palette, and ONE
 * Pointer Events drag engine (labels onto slots, circuits by their ⠿ handle).
 * HTML5 drag events do not fire on phones, so they appear here only on the two
 * FILE drop zones. The company logo lives with the size/print card.
 *
 * CONTRACT-DEVIATION (§8 item 10): the file is past ~1,400 lines — v1.1 adds the
 * photo, palette, drag and logo pieces and agent D owns only this one file.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1e2328", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", faint: "#5a6374",
  red: "#e5484d", green: "#46a758", yellow: "#f5d90a", blue: "#3b82f6", orange: "#f59e0b",
};
const LS_KEY = (id) => `elecbid_panel_${id}`;
const SAVE_DEBOUNCE_MS = 1500;

async function api(action, body = {}) {
  let r;
  try {
    r = await apiFetch("/api/panel-schedule", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...body }),
    });
  } catch {
    return { ok: false, error: "No connection — your work is saved on this phone and will sync when you have signal", offline: true };
  }
  const d = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
  // The HTTP status rides along (additive) so the logo block can tell a 403
  // ("you're not an admin") from any other failure.
  if (d && typeof d === "object" && d.httpStatus === undefined) d.httpStatus = r.status;
  return d;
}

// ── Vocabulary (contract §2) ────────────────────────────────────────────────
const TYPES = ["1p", "2p", "3p", "tandem", "quad", "spare", "space", "blank"];
const TYPE_POLES = { "1p": 1, "2p": 2, "3p": 3, tandem: 1, quad: 2, spare: 1, space: 1, blank: 1 };
const TAGS = ["gfci", "afci", "ems"];
const VOLTS = ["120/240", "120/208", "208Y/120", "347/600", "600Y/347"];
const SPACE_SIZES = [12, 16, 20, 24, 30, 32, 40, 42, 60, 66, 84];
// no_amps/too_long are informational; EVERY other flag means "a human must look"
// (generic_label, occupancy_dependent, ambiguous_span, overlap, over_spaces, and
// §11.1 "uncertain" = a row read from a photo that a human must confirm). Defined
// by exclusion, exactly as the server's reviewState does, so a flag this file has
// never heard of still shows amber here instead of blocking export invisibly.
const INFO_FLAGS = new Set(["no_amps", "too_long"]);
const UNCERTAIN_FIELD_NAMES = { number: "circuit number", amps: "amps", poles: "poles", label: "label" };

// ── Standard label palette (contract §11.2 — a local copy of the server list) ─
// A label NEVER carries amps: the tech sets the breaker size. `2` marks a load
// that is inherently 240 V in a dwelling, so an empty slot takes a 2-pole.
const LABEL_GROUPS = [
  ["Kitchen", ["Kitchen Counter Receptacles N", "Kitchen Counter Receptacles S", "Kitchen Counter Receptacles E", "Kitchen Counter Receptacles W", "Kitchen Receptacles", "Refrigerator", "Dishwasher", "Microwave", ["Range", 2], ["Cooktop", 2], ["Wall Oven", 2], "Range Hood", "Garburator", "Island Receptacles"]],
  ["Laundry & utility", [["Dryer", 2], "Washer", "Laundry Receptacles", ["Hot Water Tank", 2], "Furnace", ["Air Handler", 2], "Boiler", "HRV", "Central Vac", "Sump Pump", ["Well Pump", 2], "Septic Pump", "Septic Alarm", "Water Softener", "Freezer"]],
  ["Heating & cooling", [["A/C", 2], ["Heat Pump", 2], ["Baseboard Heat", 2], ["In-Floor Heat", 2], ["Mini-Split", 2], "Bathroom Fan Heater", "Gas Fireplace"]],
  ["Living", ["Living Room Receptacles", "Dining Room Receptacles", "Family Room Receptacles", "Office Receptacles", "Hall Receptacles", "Hall Lights", "Main Floor Lights", "Upstairs Lights", "Basement Lights", "Basement Receptacles", "Stair Lights"]],
  ["Bedrooms", ["Primary Bedroom Receptacles", "Bedroom 2 Receptacles", "Bedroom 3 Receptacles", "Bedroom 4 Receptacles", "Bedroom Lights"]],
  ["Bathrooms", ["Bathroom Receptacle GFCI", "Ensuite Receptacle GFCI", "Bathroom Lights & Fan"]],
  ["Outdoor & garage", ["Outdoor Receptacles GFCI", "Exterior Lights", "Garage Receptacles", "Garage Door Opener", "Shop Receptacles", "Shop Lights", ["EV Charger", 2], ["Hot Tub", 2], ["Pool Pump", 2], ["RV Receptacle", 2], ["Welder", 2], ["Compressor", 2], "Irrigation", "Gate Operator"]],
  ["Life safety & low voltage", ["Smoke Alarms", "Smoke & CO Alarms", "Security System", "Network / Data", "Doorbell Transformer"]],
  ["Service", [["Sub Panel", 2], ["Generator Inlet", 2], ["Surge Protector", 2], ["Solar Inverter", 2], "Spare", "Space"]],
];
const STANDARD_LABELS = LABEL_GROUPS.flatMap(([group, items]) => items.map((it) => (Array.isArray(it) ? { group, label: it[0], poles: it[1] } : { group, label: it })));
const MY_LABELS_KEY = "elecbid_panel_my_labels";
const MY_GROUP = "My labels";
const PHOTO_SOURCE_NAMES = { "jobber-live": "Jobber", upload: "ElecBid upload", archive: "archived" };
// A 2000 px JPEG at q 0.85 usually lands well under this, but a busy, sharp photo
// can exceed it — preparePhoto then steps the long edge down until it fits. The
// cap keeps the base64 JSON body (4/3 × the bytes ≈ 3.7 MB) inside Vercel's
// 4.5 MB request limit and under the server's 4 MB decoded ceiling.
const PHOTO_MAX_BYTES = 2800000;
const PHOTO_STEPS = [[2000, 0.85], [1600, 0.85], [1280, 0.8]]; // [long edge px, JPEG quality]
const LOGO_MAX_BYTES = 1000000;

// ── Presets (contract §4; the server's table is authoritative) ──────────────
const PRESETS = [
  { key: "ql62_universal", name: "QL 62 mm strip (auto length)", width_mm: 62, length_mm: null, layout: "two_col", rows: null, status: "ship" },
  { key: "ql62_wide_pair", name: "QL 62 mm wide pair (odd | even strips)", width_mm: 62, length_mm: null, layout: "one_col_pair", rows: null, status: "ship" },
  { key: "letter_sheet", name: "Letter sheet (job record / inspector)", width_mm: 215.9, length_mm: 279.4, layout: "letter", rows: null, status: "ship" },
  { key: "custom", name: "Custom size (w × h mm)", width_mm: null, length_mm: null, layout: "custom", rows: null, status: "ship" },
  { key: "ql62_sqd_lsdl_42", name: "Square D LSDL 1–42 (62 × 273)", width_mm: 62, length_mm: 273, layout: "two_col", rows: 21, status: "nominal" },
  { key: "ql62_sqd_60", name: "Square D 1–60 (62 × 305)", width_mm: 62, length_mm: 305, layout: "two_col", rows: 30, status: "nominal" },
  { key: "ql62_siemens_seq_40", name: "Siemens SEQ 1–40 (62 × 222)", width_mm: 62, length_mm: 222, layout: "two_col", rows: 20, status: "nominal" },
  { key: "ql62_leviton_lp_42", name: "Leviton LP 1–42 (62 × 178)", width_mm: 62, length_mm: 178, layout: "two_col", rows: 21, status: "nominal" },
];
const QL = { width: 62, printable: 58, pitch: 10, header: 22, footer: 6, side: 2, lead: 3, cap: 305 };
const PT_PER_MM = 2.8346;
const FONT_FLOOR_PT = 6;
// Text column widths (mm) inside the 58 mm printable: two_col = 6 number + 23 text
// per column; one_col_pair = 8 number + ~48 text (plan §7.2).
const TEXT_MM = { two_col: 23, one_col_pair: 48, letter: 70, custom: 23 };

// ── Layout math (contract §3, mirrored from api/_panelScheduleDoc.js) ───────
function circuitsOf(schedule) { return Array.isArray(schedule?.circuits) ? schedule.circuits : []; }

// Slots a head occupies. tandem and quad share one 1" slot per head (contract §2:
// a quad is stored as two adjacent heads), so they never spill into N+2.
function slotsOf(c) {
  const n = Number(c?.number) || 0;
  if (!n) return [];
  const poles = c.type === "tandem" || c.type === "quad" ? 1 : Math.max(1, Number(c.poles) || TYPE_POLES[c.type] || 1);
  const out = [];
  for (let i = 0; i < poles; i++) out.push(n + 2 * i);
  return out;
}
function deriveOccupancy(schedule) {
  const occ = {};
  for (const c of circuitsOf(schedule)) for (const s of slotsOf(c)) if (occ[s] === undefined) occ[s] = c.number;
  return occ;
}
function maxOccupied(schedule) {
  let m = 0;
  for (const c of circuitsOf(schedule)) for (const s of slotsOf(c)) if (s > m) m = s;
  return m;
}
function derivedSpaces(schedule) {
  const set = Number(schedule?.panel?.spaces);
  if (set > 0) return set;
  const m = maxOccupied(schedule);
  return SPACE_SIZES.find((s) => s >= m) || SPACE_SIZES[SPACE_SIZES.length - 1];
}
function rowsFor(spaces) { return Math.max(1, Math.ceil((Number(spaces) || 0) / 2)); }
function lengthAuto(rows, pitch = QL.pitch, header = QL.header, footer = QL.footer) { return header + rows * pitch + footer; }
function phaseOf(number, phases) {
  const row = Math.ceil(number / 2) - 1;
  return Number(phases) === 3 ? ["A", "B", "C"][row % 3] : ["L1", "L2"][row % 2];
}
function isReviewFlag(f) { return !INFO_FLAGS.has(String(f)); }
function reviewRows(schedule) { return circuitsOf(schedule).filter((c) => (c.flags || []).some(isReviewFlag)); }
// Unplaced lines still waiting on a human. `reviewed:true` (set by the server's
// accept "all") takes a line out of the count without deleting the tech's words.
function openUnplaced(schedule) { return (Array.isArray(schedule?.unplaced) ? schedule.unplaced : []).filter((u) => !(u && u.reviewed)); }
// Twin of the server's reviewState (api/panel-schedule.js). Used ONLY while the
// document on screen is ahead of the server; once a get / save / accept response
// has been adopted, the row's own needs_review is the answer (see the Designer).
function needsReviewLocal(schedule) {
  return reviewRows(schedule).length > 0 || (!!schedule?.parse?.used_claude && openUnplaced(schedule).length > 0);
}
// Twin of the server's accept, for the one case its response can't simply be
// adopted: the tech edited while it was in flight. Rows the server confirmed —
// and that still read the same here — lose their review flags locally, so the
// re-save that carries the edit carries the acceptance too. A row that was
// changed, moved or added meanwhile keeps its flags and is accepted again.
function rowSignature(c) { return JSON.stringify([c.number, c.type, Number(c.poles) || 1, c.amps ?? null, c.label ?? null, c.description ?? null, c.sub || null]); }
function acceptLocally(local, server, numbers) {
  const wanted = numbers === "all" ? null : new Set((Array.isArray(numbers) ? numbers : [numbers]).map(Number));
  const confirmed = new Map();
  for (const c of circuitsOf(server)) {
    if (c.accepted === true && (!wanted || wanted.has(c.number)) && !(c.flags || []).some(isReviewFlag)) confirmed.set(c.number, rowSignature(c));
  }
  const circuits = circuitsOf(local).map((c) => (confirmed.get(c.number) === rowSignature(c) ? { ...c, flags: (c.flags || []).filter((f) => !isReviewFlag(f)), accepted: true } : c));
  const seen = new Set((Array.isArray(server?.unplaced) ? server.unplaced : []).filter((u) => u && u.reviewed).map((u) => u.raw));
  const unplaced = (Array.isArray(local?.unplaced) ? local.unplaced : []).map((u) => (u && typeof u === "object" && !u.reviewed && seen.has(u.raw) ? { ...u, reviewed: true } : u));
  return { ...local, circuits, unplaced };
}
function printedLabel(c) {
  if (!c) return "";
  if (c.type === "spare") return c.label || "Spare";
  if (c.type === "space" || c.type === "blank") return "";
  return c.label || c.description || "";
}
// Rough fit check: real labels (caps, W/M, digits) average ~0.6 em in Helvetica,
// so at the 6 pt floor a character is ~1.27 mm — 18 chars per 23 mm line, 37 per
// 48 mm. Two lines fit when the pitch leaves room. The server's renderer measures
// for real; this only decides when to warn early.
function labelCapacity(layout, pitch_mm) {
  const perLine = Math.floor((TEXT_MM[layout] || 23) / (FONT_FLOOR_PT * 0.6 / PT_PER_MM));
  const lines = pitch_mm >= 8 ? 2 : 1;
  return perLine * lines;
}

// Split row for a strip over the 305 mm cap (twin of the server's splitRow):
// half way, nudged up to 2 rows either side so no multi-pole head straddles the
// cut. Rows are 1-based here; the cut is rows 1..k | k+1..rows.
function splitRow(rows, occ) {
  const k0 = Math.ceil(rows / 2);
  const straddles = (kk) => {
    for (const n of [2 * kk - 1, 2 * kk]) {           // numbers in row kk
      const head = occ[n];
      if (head !== undefined && occ[n + 2] === head) return true; // continues into row kk+1, same side
    }
    return false;
  };
  for (let d = 0; d < 3; d++) {
    if (k0 + d < rows && !straddles(k0 + d)) return k0 + d;
    if (k0 - d > 0 && !straddles(k0 - d)) return k0 - d;
  }
  return k0;
}

// resolveSize twin: { preset, width_mm, length_mm, rows, pitch_mm, strips:[...], fits, warnings, layout, too_long:[numbers] }
function resolveSizeLocal(schedule, size) {
  const presetKey = size?.preset || "ql62_universal";
  const preset = PRESETS.find((p) => p.key === presetKey) || PRESETS[0];
  const spaces = derivedSpaces(schedule);
  const rowsNeeded = rowsFor(spaces);
  const warnings = [];
  let width = preset.width_mm, length = preset.length_mm, rows = preset.rows || rowsNeeded;
  let layout = preset.layout, pitch = QL.pitch;
  let overflowCard = null;
  const manual = size?.auto_length === false && Number(size?.length_mm) > 0;

  if (preset.key === "custom") {
    width = Number(size?.custom_mm?.w) || 62;
    length = Number(size?.custom_mm?.h) || lengthAuto(rowsNeeded);
    layout = width < 45 ? "one_col_pair" : "two_col";
    rows = rowsNeeded;
    pitch = (length - QL.header - QL.footer) / rows;
  } else if (preset.key === "letter_sheet") {
    rows = rowsNeeded; pitch = Math.min(10, (length - 60) / Math.max(rows, 1));
  } else if (preset.length_mm) {
    // fixed brand card: it is drawn at its own row count so it lines up with
    // the door; when the panel needs MORE rows than the card has, the server
    // squeezes every row onto the card at a reduced pitch and calls it a
    // non-fit — mirror that so the preview shows what would actually print.
    if (manual) length = Number(size.length_mm);
    pitch = (length - QL.header - QL.footer) / rows;
    if (rowsNeeded > rows) {
      overflowCard = { needs: rowsNeeded, has: rows };
      rows = rowsNeeded;
      pitch = (length - QL.header - QL.footer) / rows;
    }
  } else {
    rows = rowsNeeded;
    if (manual) { length = Number(size.length_mm); pitch = (length - QL.header - QL.footer) / rows; }
    else length = lengthAuto(rows);
  }

  let fits = true;
  if (overflowCard) { fits = false; warnings.push(`Needs ${overflowCard.needs} rows, card has ${overflowCard.has} (${spaces} spaces on a ${overflowCard.has * 2}-space card) — every row is squeezed to ${pitch.toFixed(1)} mm.`); }
  if (pitch < 6) { fits = false; warnings.push(`Row pitch ${pitch.toFixed(1)} mm is under the 6 mm floor — text won't be readable.`); }

  // Strips: the cap splits a long strip in two; the wide pair is already two.
  const strips = [];
  const isStrip = layout === "two_col" || layout === "one_col_pair";
  if (isStrip) {
    const cols = layout === "one_col_pair" ? ["odd", "even"] : ["both"];
    let segs = [[1, rows]];
    if (!manual && length > QL.cap) {
      const k = splitRow(rows, deriveOccupancy(schedule));
      segs = [[1, k], [k + 1, rows]];
      warnings.push(`Longer than ${QL.cap} mm — prints as two strips.`);
    } else if (manual && length > QL.cap) warnings.push(`Longer than ${QL.cap} mm — check the printer's continuous-length limit.`);
    for (const col of cols) for (const [a, b] of segs) {
      const n = b - a + 1;
      strips.push({ col, rowFrom: a, rowTo: b, width_mm: width, length_mm: manual || preset.length_mm ? length : lengthAuto(n, pitch) });
    }
  } else strips.push({ col: "sheet", rowFrom: 1, rowTo: rows, width_mm: width, length_mm: length });

  const cap = labelCapacity(layout, pitch);
  const tooLong = circuitsOf(schedule).filter((c) => printedLabel(c).length > cap).map((c) => c.number);
  if (tooLong.length) { fits = false; warnings.push(`${tooLong.length} label${tooLong.length === 1 ? "" : "s"} too long for this width (max ~${cap} chars).`); }

  return { preset: preset.key, width_mm: width, length_mm: strips.length > 1 && isStrip ? strips[0].length_mm : length, rows, pitch_mm: pitch, strips, fits, warnings, layout, too_long: tooLong, spaces, status: preset.status };
}

// What rides to the server as custom_mm: the custom preset's own box, or — with
// Auto length off — { w: 62, h: <typed length> } on a QL preset so the server
// derives the same pitch we show. null when the length is automatic.
function exportCustomMm(res, size) {
  if (res.preset === "custom") return size?.custom_mm || null;
  if (res.preset !== "letter_sheet" && size?.auto_length === false && Number(size?.length_mm) > 0) return { w: 62, h: Math.round(Number(size.length_mm)) };
  return null;
}

// ── Grid helpers ────────────────────────────────────────────────────────────
function lowestFree(schedule, spaces, poles = 1) {
  const occ = deriveOccupancy(schedule);
  for (let n = 1; n <= spaces; n++) {
    let ok = true;
    for (let i = 0; i < poles; i++) if (occ[n + 2 * i] !== undefined || n + 2 * i > spaces) ok = false;
    if (ok) return n;
  }
  return null;
}
// Can `head` sit at `to`? Returns null (fine) or the reason it can't. Strict: any
// occupied slot blocks, so callers pass a schedule WITHOUT the head being moved
// (a parsed circuit with the same number as an existing one must not slip through).
function moveBlocker(schedule, head, to, spaces) {
  if (!(to >= 1)) return "There's no circuit 0 — it's already at the top.";
  const occ = deriveOccupancy(schedule);
  const test = { ...head, number: to };
  for (const s of slotsOf(test)) {
    if (s > spaces) return `Circuit ${s} is past the last space (${spaces}). Add spaces in the header first.`;
    if (occ[s] !== undefined) return `Slot ${s} is taken by circuit ${occ[s]} (${printedLabel(circuitsOf(schedule).find((c) => c.number === occ[s])) || "unnamed"}).`;
  }
  return null;
}
function blankCircuit(number, type = "1p") {
  return { number, poles: TYPE_POLES[type] || 1, amps: null, type, description: null, label: null, tags: [], sub: type === "tandem" ? { a: { amps: null, label: "" }, b: { amps: null, label: "" } } : null, feeds: null, raw: null, confidence: 1, flags: [] };
}
function sortCircuits(list) { return [...list].sort((a, b) => (a.number || 0) - (b.number || 0)); }
function emptySchedule() {
  return { panel: { name: "Panel A", location: null, brand: null, volts: "120/240", phases: 1, spaces: 40, main_amps: 200, main_type: "mb", bus_amps: null, numbering: "zigzag", pitch_mm: 25.4, notes: "" }, circuits: [], unplaced: [], removed: [], mode: "create", parse: { model: null, at: null, used_claude: false, flags: 0, unplaced: 0 } };
}
function normaliseSchedule(s) {
  const base = emptySchedule();
  if (!s || typeof s !== "object") return base;
  return { ...base, ...s, panel: { ...base.panel, ...(s.panel || {}) }, circuits: sortCircuits(circuitsOf(s)), unplaced: Array.isArray(s.unplaced) ? s.unplaced : [] };
}

// A parsed schedule (pasted text OR a photo) lands on the document one of two
// ways: replace everything, or merge into the free slots and count what was
// skipped. `photo` (the parse.photo stamp) rides along so the viewer can find
// the picture again after a reload.
function applyParsed(schedule, ps, mode, spaces, photo) {
  let skipped = 0;
  if (mode === "replace") {
    const parse = photo ? { ...(ps.parse || {}), photo } : (ps.parse || schedule.parse);
    const panel = { ...schedule.panel, ...Object.fromEntries(Object.entries(ps.panel || {}).filter(([, v]) => v !== null && v !== undefined && v !== "")) };
    return { next: { ...schedule, panel, circuits: ps.circuits, unplaced: ps.unplaced, parse }, skipped };
  }
  const merged = [...circuitsOf(schedule)];
  for (const c of ps.circuits) {
    const blocked = moveBlocker({ ...schedule, circuits: merged }, c, c.number, Math.max(spaces, maxOccupied(ps)));
    if (blocked) skipped++; else merged.push(c);
  }
  const next = { ...schedule, circuits: sortCircuits(merged), unplaced: [...(schedule.unplaced || []), ...ps.unplaced] };
  // A merge must carry "this came from a Claude read" just as a replace does:
  // needs_review (here and on the server) is `review rows || (unplaced && used_claude)`,
  // so without it the lines a photo could NOT place would block export after a
  // replace but slip through after a merge into a hand-built schedule.
  if (photo || ps.parse?.used_claude === true) next.parse = { ...(schedule.parse || {}), used_claude: true, ...(photo ? { photo } : {}) };
  return { next, skipped };
}

// ── Placing a palette label / moving a circuit (contract §11.5 items 4–5) ───
// Pure: each returns { next, note } on success, { error } with the reason it
// was refused, or { noop:true }. The drag engine asks first (to colour the
// candidate slot) and again on drop, so both paths can never disagree.
function headAt(schedule, slot) {
  const h = deriveOccupancy(schedule)[slot];
  return h === undefined ? null : circuitsOf(schedule).find((c) => c.number === h) || null;
}
function placeLabel(schedule, spaces, item, slot) {
  const label = String(item?.label || "").trim();
  if (!label) return { error: "That label is empty." };
  if (!(slot >= 1) || slot > spaces) return { error: `Circuit ${slot} is past the last space (${spaces}). Add spaces in the header first.` };
  const all = circuitsOf(schedule);
  const head = headAt(schedule, slot);
  const kind = /^spare$/i.test(label) ? "spare" : /^space$/i.test(label) ? "space" : "load";
  const rest = head ? all.filter((c) => c !== head) : all;
  const put = (c) => ({ ...schedule, circuits: sortCircuits([...rest, c]) });
  if (head && head.type !== "space" && head.type !== "blank") {
    // An existing breaker: only the words change — amps, poles, tags and flags stay.
    // (A breaker can't become "Space" by relabelling; the contract leaves that case open.)
    if (kind === "space") return { error: `Slot ${slot} has a breaker (${printedLabel(head) || "unnamed"}) — clear the row first to mark it as a space.` };
    if (head.type === "tandem") {
      const half = !String(head.sub?.a?.label || "").trim() ? "a" : !String(head.sub?.b?.label || "").trim() ? "b" : null;
      if (!half) return { error: `Circuit ${head.number} is a tandem with both halves named — tap the row to rename a half.` };
      const sub = { a: { amps: null, label: "", ...(head.sub?.a || {}) }, b: { amps: null, label: "", ...(head.sub?.b || {}) } };
      sub[half] = { ...sub[half], label };
      return { next: put({ ...head, sub }), note: `Circuit ${head.number}${half.toUpperCase()}: ${label}.` };
    }
    const type = head.type === "spare" && kind === "load" ? (Number(head.poles) === 3 ? "3p" : Number(head.poles) === 2 ? "2p" : "1p") : head.type;
    return { next: put({ ...head, type, label, description: label }), note: `Circuit ${head.number} relabelled ${label}.` };
  }
  // An empty slot (or a space / blank placeholder, which the label replaces).
  if (kind === "spare") return { next: put({ ...blankCircuit(slot, "spare"), label: "Spare", source: "designer" }), note: `Circuit ${slot}: Spare.` };
  if (kind === "space") return { next: put({ ...blankCircuit(slot, "space"), source: "designer" }), note: `Circuit ${slot} marked as a space.` };
  const occ = deriveOccupancy({ circuits: rest });
  const two = Number(item.poles) === 2 && slot + 2 <= spaces && occ[slot + 2] === undefined;
  const squeezed = Number(item.poles) === 2 && !two;
  return {
    next: put({ ...blankCircuit(slot, two ? "2p" : "1p"), label, description: label, source: "designer" }),
    note: two ? `Circuits ${slot}–${slot + 2}: ${label} (2-pole). Set the amps on the row.` : squeezed ? `Circuit ${slot}: ${label} — placed as 1-pole because slot ${slot + 2} isn't free.` : `Circuit ${slot}: ${label}.`,
  };
}
function moveCircuit(schedule, spaces, from, slot) {
  const all = circuitsOf(schedule);
  const head = all.find((c) => c.number === from);
  if (!head) return { error: "That circuit isn't on the schedule any more." };
  if (slot === head.number) return { noop: true };
  const others = all.filter((c) => c !== head);
  const target = headAt(schedule, slot);
  if (target && target !== head) {
    const a = slotsOf(head).length, b = slotsOf(target).length;
    if (a !== b) {
      const why = moveBlocker({ ...schedule, circuits: others }, head, slot, spaces) || `Slot ${slot} is taken by circuit ${target.number}.`;
      return { error: `${why} A swap needs the same number of poles (${a} vs ${b}).` };
    }
    const rest = others.filter((c) => c !== target);
    const movedA = { ...head, number: target.number }, movedB = { ...target, number: head.number };
    const blocked = moveBlocker({ ...schedule, circuits: rest }, movedA, movedA.number, spaces) || moveBlocker({ ...schedule, circuits: [...rest, movedA] }, movedB, movedB.number, spaces);
    if (blocked) return { error: blocked };
    return { next: { ...schedule, circuits: sortCircuits([...rest, movedA, movedB]) }, note: `Swapped circuits ${head.number} and ${target.number}.` };
  }
  const blocked = moveBlocker({ ...schedule, circuits: others }, head, slot, spaces);
  if (blocked) return { error: blocked };
  return { next: { ...schedule, circuits: sortCircuits([...others, { ...head, number: slot }]) }, note: `Moved circuit ${head.number} to ${slot}.` };
}
// payload: { kind:"circuit", number } from a row handle, or { kind:"label", label, poles } from a chip.
function dropResult(schedule, payload, slot) {
  if (!schedule || !payload) return { error: "Nothing to place." };
  const spaces = derivedSpaces(schedule);
  return payload.kind === "circuit" ? moveCircuit(schedule, spaces, payload.number, slot) : placeLabel(schedule, spaces, payload, slot);
}

// ── "My labels" (this device only; localStorage always in try/catch) ────────
// Keyed per company so two orgs signed in on one phone never share a list.
const myLabelsKey = (org) => `${MY_LABELS_KEY}:${org || "default"}`;
function loadMyLabels(org) {
  try {
    const raw = JSON.parse(localStorage.getItem(myLabelsKey(org)) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw.map((x) => (typeof x === "string" ? { label: x } : x)).filter((x) => x && typeof x.label === "string" && x.label.trim())
      .slice(0, 80).map((x) => ({ group: MY_GROUP, label: x.label.trim().slice(0, 40), ...(Number(x.poles) === 2 ? { poles: 2 } : {}) }));
  } catch { return []; }
}
function saveMyLabels(org, list) {
  try { localStorage.setItem(myLabelsKey(org), JSON.stringify(list.map((x) => (x.poles === 2 ? { label: x.label, poles: 2 } : { label: x.label })))); } catch { /* private mode — the list lasts for this visit */ }
}

// ── Browser image prep (the downscalePhoto idiom from GeneratorReport.jsx) ──
async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* older engine — try the plain call */ }
    try { return await createImageBitmap(file); } catch { /* fall through to <img> */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("This device can't open that image — try a JPEG or PNG.")); };
    img.src = url;
  });
}
// Draw `file` no larger than maxLong (longest edge) / maxWide (width) and encode it.
async function shrinkImage(file, { maxLong, maxWide, type, quality }) {
  const src = await loadBitmap(file);
  const w = src.width || 1, h = src.height || 1;
  const scale = Math.min(1, maxLong ? maxLong / Math.max(w, h) : 1, maxWide ? maxWide / w : 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (type === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); } // JPEG has no alpha
  ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  if (typeof src.close === "function") src.close();
  const blob = await new Promise((res) => canvas.toBlob(res, type, quality));
  if (!blob) throw new Error("Couldn't process that image.");
  return { blob, scaled: scale < 1 };
}
// Panel photo → JPEG, longest edge ≤ 2000 px, q 0.85 (§11.5 item 1). Still over
// 2.8 MB after that → re-encode at 1600 px, then 1280 px, so upload-photo never
// has to refuse it for size.
async function preparePhoto(file) {
  try {
    for (const [maxLong, quality] of PHOTO_STEPS) {
      const { blob } = await shrinkImage(file, { maxLong, type: "image/jpeg", quality });
      if (blob.size <= PHOTO_MAX_BYTES) return { blob, contentType: "image/jpeg" };
    }
    throw new Error("That photo is too large to upload even after shrinking.");
  } catch (e) {
    // Can't decode it here (rare) — send the original only if the server takes it as-is.
    if (/^image\/(jpeg|png|webp)$/.test(file.type || "") && file.size <= PHOTO_MAX_BYTES) return { blob: file, contentType: file.type };
    throw e;
  }
}
// Logo → ≤ 600 px wide; a PNG stays a PNG (keeps its transparency), anything else is JPEG.
async function prepareLogo(file) {
  const png = /png$/i.test(file.type || "");
  const type = png ? "image/png" : "image/jpeg";
  let { blob, scaled } = await shrinkImage(file, { maxWide: 600, type, quality: 0.9 });
  if (!scaled && file.size <= LOGO_MAX_BYTES && /^image\/(png|jpeg)$/i.test(file.type || "")) return { blob: file, contentType: type }; // already small — keep the original bytes
  let contentType = type;
  if (blob.size > LOGO_MAX_BYTES) { ({ blob } = await shrinkImage(file, { maxWide: 600, type: "image/jpeg", quality: 0.85 })); contentType = "image/jpeg"; }
  if (blob.size > LOGO_MAX_BYTES) throw new Error("That logo is over 1 MB even at 600 px — try a simpler file.");
  return { blob, contentType };
}
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || "").replace(/^data:[^,]*,/, ""));
    fr.onerror = () => reject(new Error("Couldn't read that file."));
    fr.readAsDataURL(blob);
  });
}
function firstImageFile(list) { return Array.from(list || []).find((f) => /^image\//.test(f.type || "")) || null; }

// ── Pointer drag engine (contract §11.5 item 6) ─────────────────────────────
// One code path for mouse and touch: capture the pointer on the handle/chip
// (the only elements with touch-action:none, so the page still scrolls
// everywhere else), float a ghost under the pointer, and ask
// document.elementFromPoint which [data-slot] is under it. Drops on pointerup;
// pointercancel / Escape / a lost capture cancel. No HTML5 drag events.
// The dragged chip / handle can be UNMOUNTED mid-drag (a second finger presses
// the palette's Hide, a group tab, or removes a My-label). Its own listeners die
// with it and lostpointercapture then fires on the document, so the engine also
// watches the document / window and checks el.isConnected every frame —
// otherwise the ghost would stay up and every later drag would be refused.
// How far a press may wander before it becomes a drag. A finger slides more than
// a mouse: at 6 px a slightly sliding tap on a chip turned into a tiny drag (and
// a drop on nothing) instead of arming the chip.
const DRAG_SLOP_PX = { mouse: 6, touch: 12 };
const DRAG_EDGE_PX = 60;
const NO_SELECT = { touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" };
function scrollerOf(el) {
  for (let p = el ? el.parentElement : null; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const oy = window.getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null; // the window scrolls
}
function slotAt(x, y) {
  const el = document.elementFromPoint(x, y);
  const hit = el && el.closest ? el.closest("[data-slot]") : null;
  const n = hit ? Number(hit.getAttribute("data-slot")) : 0;
  return n > 0 ? n : null;
}
function useSlotDrag({ check, onDrop }) {
  const [drag, setDrag] = useState(null); // { payload, slot, blocked } while a drag is live
  const live = useRef(null);
  const lastEnd = useRef({ at: 0, el: null });
  const cb = useRef({ check, onDrop });
  useEffect(() => { cb.current = { check, onDrop }; });

  const finish = useCallback((commit) => {
    const s = live.current;
    if (!s) return;
    live.current = null;
    s.el.removeEventListener("pointermove", s.move);
    s.el.removeEventListener("pointerup", s.up);
    s.el.removeEventListener("pointercancel", s.cancel);
    s.el.removeEventListener("lostpointercapture", s.cancel);
    document.removeEventListener("lostpointercapture", s.gone);
    window.removeEventListener("pointerup", s.gone, true);
    window.removeEventListener("pointercancel", s.gone, true);
    window.removeEventListener("keydown", s.key, true);
    cancelAnimationFrame(s.raf);
    try { s.el.releasePointerCapture(s.pointerId); } catch { /* already released */ }
    if (s.ghost) s.ghost.remove();
    if (!s.dragging) return; // a plain tap — the element's own onClick handles it
    lastEnd.current = { at: Date.now(), el: s.el };
    document.body.style.userSelect = s.prevSelect;
    setDrag(null);
    if (commit && s.slot) cb.current.onDrop(s.payload, s.slot);
  }, []);
  useEffect(() => () => finish(false), [finish]);

  const begin = useCallback((e, payload, text) => {
    // A press whose element was unmounted before it ever became a drag has no
    // frame loop to notice — clear it here so it can't block this new one.
    if (live.current && !live.current.el.isConnected) finish(false);
    if (live.current || e.isPrimary === false || (e.pointerType === "mouse" && e.button !== 0)) return;
    const el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
    const touch = e.pointerType !== "mouse";
    const slop = e.pointerType === "touch" ? DRAG_SLOP_PX.touch : DRAG_SLOP_PX.mouse;
    const s = { el, pointerId: e.pointerId, payload, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, slop, dragging: false, slot: null, ghost: null, raf: 0, scroller: scrollerOf(el), prevSelect: "" };
    const retarget = () => {
      const slot = slotAt(s.x, s.y);
      if (slot === s.slot) return;
      s.slot = slot;
      setDrag({ payload, slot, blocked: slot ? cb.current.check(payload, slot) : null });
    };
    const tick = () => {
      if (live.current !== s) return;
      if (!s.el.isConnected) { finish(false); return; } // the chip / handle was unmounted mid-drag
      // Auto-scroll while the pointer sits within 60 px of the top/bottom edge.
      const box = s.scroller ? s.scroller.getBoundingClientRect() : null;
      const top = box ? Math.max(0, box.top) : 0;
      const bottom = box ? Math.min(window.innerHeight, box.bottom) : window.innerHeight;
      let dy = 0;
      if (s.y < top + DRAG_EDGE_PX) dy = -(2 + Math.ceil((top + DRAG_EDGE_PX - s.y) / 4));
      else if (s.y > bottom - DRAG_EDGE_PX) dy = 2 + Math.ceil((s.y - (bottom - DRAG_EDGE_PX)) / 4);
      if (dy) { (s.scroller || window).scrollBy(0, Math.max(-18, Math.min(18, dy))); retarget(); }
      s.raf = requestAnimationFrame(tick);
    };
    s.move = (ev) => {
      if (ev.pointerId !== s.pointerId) return;
      s.x = ev.clientX; s.y = ev.clientY;
      if (!s.dragging) {
        if (Math.hypot(s.x - s.x0, s.y - s.y0) < s.slop) return;
        s.dragging = true;
        s.prevSelect = document.body.style.userSelect;
        document.body.style.userSelect = "none";
        s.ghost = document.createElement("div");
        s.ghost.textContent = text;
        s.ghost.style.cssText = `position:fixed;left:0;top:0;z-index:1000;pointer-events:none;max-width:230px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:7px 12px;border-radius:999px;background:${C.lime};color:#10130a;font-size:12.5px;font-weight:800;box-shadow:0 8px 22px rgba(0,0,0,.55);will-change:transform`;
        document.body.appendChild(s.ghost);
        setDrag({ payload, slot: null, blocked: null });
        s.raf = requestAnimationFrame(tick);
      }
      // Touch: above the finger so it isn't hidden; mouse: just off the cursor.
      s.ghost.style.transform = touch ? `translate(${Math.max(4, s.x - 60)}px, ${s.y - 58}px)` : `translate(${s.x + 14}px, ${s.y + 12}px)`;
      retarget();
    };
    s.up = (ev) => { if (ev.pointerId === s.pointerId) finish(true); };
    s.cancel = (ev) => { if (ev.pointerId === s.pointerId) finish(false); };
    s.key = (ev) => { if (ev.key === "Escape") { ev.preventDefault(); finish(false); } };
    // Only acts once the element is gone: while it is connected its own
    // listeners above own the pointer (and a drop must stay a drop).
    s.gone = (ev) => { if (ev.pointerId === s.pointerId && !s.el.isConnected) finish(false); };
    live.current = s;
    el.addEventListener("pointermove", s.move);
    el.addEventListener("pointerup", s.up);
    el.addEventListener("pointercancel", s.cancel);
    el.addEventListener("lostpointercapture", s.cancel);
    document.addEventListener("lostpointercapture", s.gone);
    window.addEventListener("pointerup", s.gone, true);
    window.addEventListener("pointercancel", s.gone, true);
    window.addEventListener("keydown", s.key, true);
  }, [finish]);

  // A click on the dragged element right after a real drag is that drag's own
  // pointerup, not a tap (a click on any OTHER element is always a real tap).
  const justEnded = useCallback((el) => lastEnd.current.el === el && Date.now() - lastEnd.current.at < 400, []);
  return { drag, begin, justEnded };
}

// ── Atoms ───────────────────────────────────────────────────────────────────
function Btn({ children, kind, disabled, onClick, style, title }) {
  const base = {
    padding: "10px 15px", borderRadius: 8, fontSize: 13.5, fontWeight: 700, minHeight: 40,
    border: `1px solid ${kind === "pri" ? C.lime : kind === "danger" ? C.red : C.border}`,
    background: kind === "pri" ? C.lime : kind === "danger" ? "rgba(229,72,77,.12)" : C.card,
    color: kind === "pri" ? "#10130a" : kind === "danger" ? C.red : C.text,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
  };
  return <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...style }}>{children}</button>;
}
const inputStyle = { background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 10px", fontSize: 14, width: "100%", boxSizing: "border-box", minHeight: 40 };
function Field({ label, children, span }) {
  return (
    <label style={{ display: "block", gridColumn: span ? "1 / -1" : undefined, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.faint, letterSpacing: .4, marginBottom: 4 }}>{label.toUpperCase()}</div>
      {children}
    </label>
  );
}
function Shell({ children }) {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "16px 14px 120px", color: C.text }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>{children}</div>
    </div>
  );
}
function Card({ title, children, right }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 9 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: .4 }}>{title.toUpperCase()}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
// `actions` (optional) = buttons under the text — Retry, "use the last saved copy".
function Banner({ kind, text, onClose, actions }) {
  const col = kind === "err" ? C.red : kind === "warn" ? C.orange : C.blue;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(255,255,255,.03)", border: `1px solid ${col}`, borderRadius: 8, padding: "9px 11px", marginTop: 8 }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.text, lineHeight: 1.45 }}>
        {text}
        {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>{actions}</div>}
      </div>
      {onClose && <button onClick={onClose} style={{ background: "none", border: "none", color: col, cursor: "pointer", fontSize: 15, fontWeight: 800, padding: 0, lineHeight: 1 }}>×</button>}
    </div>
  );
}
function SaveChip({ stateName, rev }) {
  const map = {
    idle: { t: rev ? `rev ${rev}` : "", c: C.faint },
    dirty: { t: "Unsaved edits", c: C.faint },
    saving: { t: "Saving…", c: C.blue },
    saved: { t: `Saved ✓${rev ? ` · rev ${rev}` : ""}`, c: C.green },
    offline: { t: "Saved on this phone — will sync", c: C.yellow },
  };
  const m = map[stateName] || map.idle;
  if (!m.t) return null;
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: m.c, border: `1px solid ${m.c}`, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>{m.t}</span>;
}
function Chip({ on, children, onClick, color }) {
  const col = color || C.lime;
  return (
    <button onClick={onClick} style={{ padding: "8px 12px", minHeight: 40, borderRadius: 999, cursor: "pointer", fontSize: 12.5, fontWeight: 700, border: `1px solid ${on ? col : C.border}`, background: on ? "rgba(165,206,58,.16)" : C.surface, color: on ? col : C.muted }}>
      {children}
    </button>
  );
}
function useScale() {
  // 1 mm = 2 px on a phone, 3 px on a desktop — the preview is to scale either way.
  const calc = () => (typeof window !== "undefined" && window.innerWidth >= 720 ? 3 : 2);
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    const onResize = () => setScale(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return scale;
}

// A thumb needs a 40 px target: true on a phone-width screen, or with any coarse
// (touch) pointer on a wider one.
function useThumbTargets() {
  const calc = () => typeof window !== "undefined" && (window.innerWidth < 720 || (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches));
  const [on, setOn] = useState(calc);
  useEffect(() => {
    const onResize = () => setOn(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return on;
}

// ── SVG strip preview (to scale) ────────────────────────────────────────────
function truncate(s, max) { return s.length > max ? `${s.slice(0, Math.max(1, max - 1))}…` : s; }
function StripSvg({ schedule, strip, size, scale, meta }) {
  // A logo that won't load falls back to the company-name text (as the renderer does).
  const [logoBad, setLogoBad] = useState(null);
  const logo = meta.logoUrl && logoBad !== meta.logoUrl ? meta.logoUrl : null;
  const mm = (v) => v * scale;
  const W = mm(strip.width_mm), H = mm(strip.length_mm);
  const isSheet = strip.col === "sheet";
  const side = isSheet ? mm(12) : mm(QL.side), lead = isSheet ? mm(12) : mm(QL.lead);
  const printW = W - 2 * side;
  const headerH = mm(isSheet ? 30 : QL.header);
  const pitch = mm(size.pitch_mm);
  const rows = strip.rowTo - strip.rowFrom + 1;
  const occ = deriveOccupancy(schedule);
  const byNum = {};
  for (const c of circuitsOf(schedule)) byNum[c.number] = c;
  const font = Math.max(6, Math.min(11, pitch * 0.36));
  const twoLines = pitch >= 2 * font + 3;
  const numW = strip.col === "both" ? mm(6) : mm(8);
  const gridTop = lead + headerH;
  const panel = schedule.panel || {};

  // Column geometry: two_col = [text][num][num][text]; one_col_pair puts the
  // numbers on the side that faces the breakers.
  const cols = [];
  if (strip.col === "both") {
    const textW = (printW - 2 * numW) / 2;
    cols.push({ parity: 1, textX: side, textW, numX: side + textW });
    cols.push({ parity: 0, textX: side + textW + 2 * numW, textW, numX: side + textW + numW });
  } else if (strip.col === "odd") cols.push({ parity: 1, textX: side, textW: printW - numW, numX: side + printW - numW });
  else if (strip.col === "even") cols.push({ parity: 0, textX: side + numW, textW: printW - numW, numX: side });
  else {
    const textW = (printW - 2 * numW) / 2;
    cols.push({ parity: 1, textX: side, textW, numX: side + textW });
    cols.push({ parity: 0, textX: side + textW + 2 * numW, textW, numX: side + textW + numW });
  }
  const charPx = font * 0.55;

  const cells = [];
  for (const col of cols) {
    for (let r = strip.rowFrom; r <= strip.rowTo; r++) {
      const n = (r - 1) * 2 + (col.parity ? 1 : 2);
      const y = gridTop + (r - strip.rowFrom) * pitch;
      const headNum = occ[n];
      const head = headNum !== undefined ? byNum[headNum] : null;
      // number cell — always drawn
      cells.push(<rect key={`n${n}`} x={col.numX} y={y} width={numW} height={pitch} fill="#111" stroke="#444" strokeWidth={0.5} />);
      cells.push(<text key={`nt${n}`} x={col.numX + numW / 2} y={y + pitch / 2 + font * 0.35} fontSize={font} fontWeight="700" fill="#fff" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif">{n}</text>);
      if (head && head.number !== n) continue; // continuation row — covered by the merged cell below
      const span = head ? slotsOf(head).filter((s) => Math.ceil(s / 2) >= strip.rowFrom && Math.ceil(s / 2) <= strip.rowTo).length : 1;
      const h = pitch * Math.max(1, span);
      const flagged = head && (head.flags || []).some(isReviewFlag);
      const fill = flagged ? "rgba(245,158,11,.28)" : head?.type === "spare" ? "#e6e6e6" : "#fff";
      cells.push(<rect key={`c${n}`} x={col.textX} y={y} width={col.textW} height={h} fill={fill} stroke="#666" strokeWidth={0.5} />);
      if (!head || head.type === "space" || head.type === "blank") continue;
      const pad = Math.max(2, font * 0.3);
      const maxChars = Math.max(2, Math.floor((col.textW - 2 * pad) / charPx));
      if (head.type === "tandem") {
        cells.push(<line key={`tl${n}`} x1={col.textX} y1={y + h / 2} x2={col.textX + col.textW} y2={y + h / 2} stroke="#999" strokeWidth={0.5} />);
        const f2 = Math.max(5, font * 0.8);
        cells.push(<text key={`ta${n}`} x={col.textX + pad} y={y + h / 4 + f2 * 0.35} fontSize={f2} fill="#111" fontFamily="Helvetica, Arial, sans-serif">{truncate(`${n}A ${head.sub?.a?.label || ""}`.trim(), maxChars)}</text>);
        cells.push(<text key={`tb${n}`} x={col.textX + pad} y={y + (3 * h) / 4 + f2 * 0.35} fontSize={f2} fill="#111" fontFamily="Helvetica, Arial, sans-serif">{truncate(`${n}B ${head.sub?.b?.label || ""}`.trim(), maxChars)}</text>);
        continue;
      }
      const label = printedLabel(head);
      const amps = head.amps ? `${head.amps}A${head.poles > 1 ? `/${head.poles}P` : ""}` : "";
      // Greedy wrap: once a word spills to line 2, everything after it stays there.
      let l1 = "", l2 = "";
      for (const w of label.split(" ")) {
        if (!l2 && (l1 + " " + w).trim().length <= maxChars) l1 = (l1 + " " + w).trim();
        else l2 = (l2 + " " + w).trim();
      }
      const lines = twoLines && l2 ? [l1, truncate(l2, maxChars)] : [truncate(label, maxChars)];
      const grey = head.type === "spare" ? "#777" : "#111";
      const y1 = y + h / 2 - font * 0.25, y2 = y + h / 2 + font * 0.85, yMid = y + h / 2 + font * 0.35;
      // Amps sit right of the label when there's room, else on the second line.
      const ampsChars = amps ? amps.length + 1 : 0;
      const ampsBeside = amps && lines.length === 1 && lines[0].length + ampsChars <= maxChars;
      const ampsBelow = amps && !ampsBeside && twoLines && (lines.length === 1 || lines[1].length + ampsChars <= maxChars);
      const twoRows = lines.length === 2 || ampsBelow;
      lines.forEach((t, i) => {
        cells.push(<text key={`l${n}_${i}`} x={col.textX + pad} y={twoRows ? (i === 0 ? y1 : y2) : yMid} fontSize={font} fill={grey} fontFamily="Helvetica, Arial, sans-serif">{t}</text>);
      });
      if (ampsBeside || ampsBelow) cells.push(<text key={`a${n}`} x={col.textX + col.textW - pad} y={ampsBeside ? yMid : y2} fontSize={font * 0.85} fill="#555" textAnchor="end" fontFamily="Helvetica, Arial, sans-serif">{amps}</text>);
      if (flagged) cells.push(<rect key={`fl${n}`} x={col.textX} y={y} width={Math.max(1, mm(0.6))} height={h} fill={C.orange} />);
    }
  }

  const hf = (k) => Math.max(5, headerH * k);
  const hx = side + mm(1.5);
  const volts = `${panel.volts || "120/240"} ${Number(panel.phases) === 3 ? "3Ø" : "1Ø"}`;
  const main = panel.main_amps ? `${panel.main_amps}A ${String(panel.main_type || "mb").toUpperCase()}` : panel.bus_amps ? `${panel.bus_amps}A bus` : "";
  const footY = gridTop + rows * pitch;
  // Logo (§11.3): on a strip it REPLACES the company-name line (fit inside
  // 44 × 9 mm, centred) and the phone / panel lines tighten around it; on the
  // letter sheet it sits at the left (fit 50 × 14 mm) with the text to its right.
  const onStrip = !!logo && !isSheet;
  const tight = onStrip && !!meta.phone;
  let logoBox = null;
  if (logo && isSheet) logoBox = { x: hx, y: lead + mm(1), w: Math.min(mm(50), printW * 0.4), h: Math.min(mm(14), headerH - mm(2)), fit: "xMinYMid meet" };
  else if (logo) {
    const w = Math.min(mm(44), printW - mm(3));
    logoBox = { x: side + (printW - w) / 2, y: lead + headerH * (meta.phone ? 0.21 : 0.03), w, h: Math.min(mm(9), headerH * (meta.phone ? 0.28 : 0.4)), fit: "xMidYMid meet" };
  }
  const tx = logoBox && isSheet ? hx + logoBox.w + mm(3) : hx;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", background: "#fff", borderRadius: 3, boxShadow: "0 0 0 1px #333, 0 6px 18px rgba(0,0,0,.45)", flexShrink: 0 }}>
      <rect x={0} y={0} width={W} height={H} fill="#fff" />
      <rect x={side} y={lead} width={printW} height={H - 2 * lead} fill="none" stroke="#ddd" strokeWidth={0.5} strokeDasharray="2 2" />
      {/* Real company only — never a placeholder. No phone ⇒ the name takes the top line. */}
      {meta.phone && <text x={tx} y={lead + hf(tight ? 0.17 : 0.22)} fontSize={hf(tight ? 0.16 : 0.2)} fontWeight="800" fill="#111" fontFamily="Helvetica, Arial, sans-serif">{meta.phone}</text>}
      {logoBox && <image href={logo} x={logoBox.x} y={logoBox.y} width={logoBox.w} height={logoBox.h} preserveAspectRatio={logoBox.fit} onError={() => setLogoBad(logo)} />}
      {meta.company && !onStrip && (meta.phone
        ? <text x={tx} y={lead + hf(0.4)} fontSize={hf(0.13)} fill="#333" fontFamily="Helvetica, Arial, sans-serif">{meta.company}</text>
        : <text x={tx} y={lead + hf(0.26)} fontSize={hf(0.17)} fontWeight="800" fill="#111" fontFamily="Helvetica, Arial, sans-serif">{meta.company}</text>)}
      <text x={tx} y={lead + hf(tight ? 0.63 : 0.58)} fontSize={hf(tight ? 0.14 : 0.16)} fontWeight="700" fill="#111" fontFamily="Helvetica, Arial, sans-serif">{truncate(panel.name || "Panel", 22)}</text>
      <text x={tx} y={lead + hf(tight ? 0.77 : 0.74)} fontSize={hf(0.12)} fill="#333" fontFamily="Helvetica, Arial, sans-serif">{volts}{main ? ` · ${main}` : ""}</text>
      <text x={tx} y={lead + hf(0.9)} fontSize={hf(0.11)} fill="#555" fontFamily="Helvetica, Arial, sans-serif">{meta.job ? `Job #${meta.job} · ` : ""}{meta.date}</text>
      <line x1={side} y1={gridTop} x2={side + printW} y2={gridTop} stroke="#111" strokeWidth={1} />
      {cells}
      {footY + mm(4) < H && <text x={hx} y={footY + mm(3.5)} fontSize={Math.max(5, mm(2))} fill="#666" fontFamily="Helvetica, Arial, sans-serif">Spare = unused breaker</text>}
    </svg>
  );
}

// ── Company logo (contract §11.5 item 7) ────────────────────────────────────
// Everyone sees the current logo. Upload / Replace / Remove are admin-only: the
// file input and buttons are rendered for admins alone (usePermissions — the
// same role the server checks), so a tech never picks, shrinks and posts a file
// just to be refused. The server's 403 stays as the fallback (a role changed
// mid-session, or an owner using "View As").
function LogoBlock({ logoUrl, company, onChange }) {
  const { isAdmin } = usePermissions() || {};
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [refused, setRefused] = useState(false);
  // The thumbnail's link is signed for an hour, so a page left open outlives it.
  // First failure → ask the server for a fresh link (once); if that one won't
  // load either, show the company name as text — what a card with no usable
  // logo prints — instead of a broken-image icon.
  const [logoBad, setLogoBad] = useState(null); // the URL that would not load
  const refetched = useRef(false);
  const denied = !isAdmin || refused;
  const input = useRef(null);
  const send = async (body) => {
    setBusy(true); setMsg(null);
    const r = await api("logo", body);
    setBusy(false);
    if (r.httpStatus === 403) { setRefused(true); return; }
    if (!r.ok) { setMsg(r.error || "Couldn't update the logo"); return; }
    refetched.current = false; // a new logo gets its own one re-fetch
    onChange(r.logoUrl || null);
  };
  const onThumbError = async () => {
    const failed = logoUrl;
    if (refetched.current) { setLogoBad(failed); return; }
    refetched.current = true;
    const r = await api("logo", { op: "get" });
    if (r.ok && !r.logoUrl) onChange(null);                              // the logo was removed meanwhile
    else if (r.ok && r.logoUrl !== failed) onChange(r.logoUrl);          // fresh link — the preview picks it up too
    else setLogoBad(failed);                                             // offline, refused, or the same dead link
  };
  const thumbDead = !!logoUrl && logoBad === logoUrl;
  const pick = async (file) => {
    if (!file || denied) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type || "")) { setMsg("Use a PNG or JPEG file for the logo."); return; }
    setBusy(true); setMsg(null);
    try {
      const { blob, contentType } = await prepareLogo(file);
      await send({ op: "set", dataBase64: await blobToBase64(blob), contentType });
    } catch (e) { setBusy(false); setMsg(String(e?.message || e)); }
  };
  return (
    <div style={{ marginTop: 10, padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.faint, letterSpacing: .4, marginBottom: 6 }}>COMPANY LOGO</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {!logoUrl ? <span style={{ fontSize: 12.5, color: C.muted }}>No logo yet — the company name prints as text.</span>
          : thumbDead ? <div style={{ background: "#fff", borderRadius: 6, padding: "8px 10px", fontSize: 14, fontWeight: 800, color: "#111", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title="The logo image couldn't be loaded">{company || "Company name"}</div>
          : <div style={{ background: "#fff", borderRadius: 6, padding: "5px 8px", lineHeight: 0 }}><img key={logoUrl} src={logoUrl} alt="Company logo" onError={onThumbError} style={{ display: "block", maxHeight: 44, maxWidth: 180 }} /></div>}
        {!denied && (
          <>
            <input ref={input} type="file" accept="image/png,image/jpeg" style={{ display: "none" }} onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; pick(f); }} />
            <Btn disabled={busy} onClick={() => input.current && input.current.click()} style={{ padding: "7px 11px", fontSize: 12 }}>{busy ? "Working…" : logoUrl ? "Replace" : "Upload logo"}</Btn>
            {logoUrl && <Btn kind="danger" disabled={busy} onClick={() => { if (window.confirm("Remove the company logo? Cards go back to printing the company name as text.")) send({ op: "remove" }); }} style={{ padding: "7px 11px", fontSize: 12 }}>Remove</Btn>}
          </>
        )}
      </div>
      {thumbDead && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 6 }}>The logo image couldn't be loaded here, so the company name is shown instead. Reload the page to try again.</div>}
      {denied && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Only an admin can change the company logo.</div>}
      {msg && <Banner kind="err" text={msg} onClose={() => setMsg(null)} />}
      <div style={{ fontSize: 11, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>Prints at the top of every card in place of the company name. Black on white prints best on a thermal label printer.</div>
    </div>
  );
}

// ── Size panel: preset, custom, auto-length lock, readout, warnings, preview ─
function SizePanel({ schedule, size, setSize, meta, onLogo }) {
  const scale = useScale();
  const res = useMemo(() => resolveSizeLocal(schedule, size), [schedule, size]);
  const preset = PRESETS.find((p) => p.key === res.preset) || PRESETS[0];
  const auto = size.auto_length !== false;
  // The readout names the rows the PANEL needs (not the card's), the derived
  // pitch (so a manual length shows what it did to the rows), and the verdict.
  const readout = `${Math.round(res.width_mm)} × ${Math.round(res.length_mm)} mm · ${res.rows} row${res.rows === 1 ? "" : "s"}${res.strips.length > 1 ? ` · ${res.strips.length} strips` : ""} · ${res.pitch_mm.toFixed(1)} mm pitch · ${res.fits ? "fits" : "doesn't fit"}`;
  const setPreset = (key) => {
    const p = PRESETS.find((x) => x.key === key) || PRESETS[0];
    setSize({ ...size, preset: key, custom_mm: key === "custom" ? (size.custom_mm || { w: 62, h: Math.round(lengthAuto(rowsFor(derivedSpaces(schedule)))) }) : null, auto_length: true, length_mm: p.length_mm || null });
  };
  return (
    <Card title="Size" right={<span style={{ fontSize: 12, fontWeight: 800, color: res.fits ? C.green : C.orange }}>{readout}</span>}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="Preset" span>
          <select value={res.preset} onChange={(e) => setPreset(e.target.value)} style={inputStyle}>
            {PRESETS.filter((p) => p.status === "ship").map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            {PRESETS.filter((p) => p.status !== "ship").map((p) => <option key={p.key} value={p.key}>{p.name} — unverified size</option>)}
          </select>
        </Field>
        {res.preset === "custom" && (
          <>
            <Field label="Width mm">
              <input type="number" inputMode="decimal" min={20} max={300} value={size.custom_mm?.w ?? 62} onChange={(e) => setSize({ ...size, custom_mm: { ...(size.custom_mm || {}), w: Number(e.target.value) || 0 } })} style={inputStyle} />
            </Field>
            <Field label="Height mm">
              <input type="number" inputMode="decimal" min={30} max={1000} value={size.custom_mm?.h ?? ""} onChange={(e) => setSize({ ...size, custom_mm: { ...(size.custom_mm || {}), h: Number(e.target.value) || 0 } })} style={inputStyle} />
            </Field>
          </>
        )}
        {res.preset !== "custom" && res.preset !== "letter_sheet" && (
          <>
            <Field label="Auto length">
              <Chip on={auto} onClick={() => setSize({ ...size, auto_length: !auto, length_mm: Math.round(res.length_mm) })}>{auto ? "Auto — grows with circuits" : "Manual — type a length"}</Chip>
            </Field>
            <Field label="Length mm">
              <input type="number" inputMode="decimal" disabled={auto} value={auto ? Math.round(res.length_mm) : (size.length_mm ?? "")} onChange={(e) => setSize({ ...size, length_mm: Number(e.target.value) || 0 })} style={{ ...inputStyle, opacity: auto ? 0.6 : 1 }} />
            </Field>
          </>
        )}
      </div>
      {preset.status !== "ship" && <Banner kind="warn" text="Unverified size — measure a real door label before printing a film card on this preset." />}
      {res.warnings.map((w, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Banner kind="warn" text={w} />
          {res.preset !== "ql62_wide_pair" && /label|pitch/i.test(w) && (
            <Btn onClick={() => setPreset("ql62_wide_pair")} style={{ padding: "6px 10px", fontSize: 12, minHeight: 32 }}>Switch to wide pair</Btn>
          )}
        </div>
      ))}
      {onLogo && <LogoBlock logoUrl={meta.logoUrl || null} company={meta.company} onChange={onLogo} />}
      <div style={{ fontSize: 11, color: C.faint, margin: "10px 0 6px" }}>
        Preview at {scale} px per mm · {res.strips.length === 1 ? "one strip" : `${res.strips.length} strips`} · pitch {res.pitch_mm.toFixed(1)} mm
        {res.too_long.length ? ` · too long: #${res.too_long.slice(0, 6).join(", #")}${res.too_long.length > 6 ? "…" : ""}` : ""}
      </div>
      <div style={{ display: "flex", gap: 14, overflowX: "auto", padding: "8px 4px 12px", alignItems: "flex-start" }}>
        {res.strips.map((s, i) => (
          <div key={i} style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 4 }}>
              {s.col === "sheet" ? "Letter" : s.col === "both" ? `Strip ${i + 1}` : `${s.col === "odd" ? "Odd" : "Even"} side${res.strips.length > 2 ? ` ${Math.floor(i % (res.strips.length / 2)) + 1}` : ""}`} · {Math.round(s.width_mm)} × {Math.round(s.length_mm)} mm · rows {s.rowFrom}–{s.rowTo}
            </div>
            <StripSvg schedule={schedule} strip={s} size={res} scale={s.col === "sheet" ? Math.min(scale, 2) : scale} meta={meta} />
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── The grid: odd | breaker column | even ───────────────────────────────────
function FlagTag({ flags }) {
  const shown = (flags || []).filter(isReviewFlag);
  if (!shown.length) return null;
  // "uncertain" leads: it is the one a tech has to check against the photo.
  const first = shown.includes("uncertain") ? "uncertain" : shown[0];
  return <span style={{ fontSize: 9.5, fontWeight: 800, color: C.orange, lineHeight: 1.2 }}>{first.replace(/_/g, " ")}{shown.length > 1 ? ` +${shown.length - 1}` : ""}</span>;
}
// Every slot carries data-slot="<number>" (the text cell = its head's number,
// the number cell = that exact slot) — the drag engine finds its target through
// document.elementFromPoint, never through per-cell listeners.
function Grid({ schedule, spaces, onTap, onGrab, drag, armed }) {
  const thumb = useThumbTargets();
  const rows = rowsFor(spaces);
  const occ = deriveOccupancy(schedule);
  const byNum = {};
  for (const c of circuitsOf(schedule)) byNum[c.number] = c;
  const phases = schedule.panel?.phases;
  const cand = drag && drag.slot ? drag.slot : null;                          // slot under the pointer
  const candCell = cand ? (occ[cand] !== undefined ? occ[cand] : cand) : null; // the cell that slot belongs to
  const candCol = drag && drag.blocked ? C.red : C.lime;
  const fromNum = drag && drag.payload.kind === "circuit" ? drag.payload.number : null;

  const cell = (n, parity) => {
    const headNum = occ[n];
    const head = headNum !== undefined ? byNum[headNum] : null;
    if (head && head.number !== n) return null; // continuation — the head's rowSpan covers it
    const span = head ? slotsOf(head).length : 1;
    const flagged = head && (head.flags || []).some(isReviewFlag);
    const bg = flagged ? "rgba(245,158,11,.14)" : head?.type === "spare" ? "rgba(255,255,255,.04)" : head?.type === "space" || !head ? "transparent" : C.surface;
    const border = flagged ? `solid ${C.orange}` : head ? `solid ${C.border}` : `dashed ${armed ? C.lime : C.faint}`;
    const align = parity ? "left" : "right";
    const isCand = candCell === n;
    return (
      <td key={`c${n}`} rowSpan={span} data-slot={n} style={{ padding: 3, verticalAlign: "top", width: "44%" }}>
        {/* The handle sits beside the breaker column, so the outer edges stay free for scrolling with a thumb. */}
        <div style={{ display: "flex", flexDirection: parity ? "row" : "row-reverse", alignItems: "stretch", gap: 2, borderRadius: 8, outline: isCand ? `2px solid ${candCol}` : "none", outlineOffset: -1, background: isCand ? (drag.blocked ? "rgba(229,72,77,.14)" : "rgba(165,206,58,.14)") : "transparent", opacity: fromNum !== null && head && head.number === fromNum ? 0.45 : 1 }}>
        <button onClick={() => onTap(n)} style={{ flex: 1, minWidth: 0, minHeight: 44 * span - 6, textAlign: align, cursor: "pointer", padding: "6px 8px", borderRadius: 8, background: bg, border: `1px ${border}`, color: C.text, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: parity ? "flex-start" : "flex-end", gap: 2, boxSizing: "border-box" }}>
          {!head && <span style={{ fontSize: 11.5, color: armed ? C.lime : C.faint }}>{armed ? "+ place here" : "+ add"}</span>}
          {head && head.type === "tandem" && (
            <>
              <span style={{ fontSize: 12.5, lineHeight: 1.3 }}><b style={{ color: C.faint }}>{n}A</b> {head.sub?.a?.label || "—"}{head.sub?.a?.amps ? <span style={{ color: C.muted }}> {head.sub.a.amps}A</span> : null}</span>
              <span style={{ fontSize: 12.5, lineHeight: 1.3, borderTop: `1px dashed ${C.border}`, paddingTop: 2 }}><b style={{ color: C.faint }}>{n}B</b> {head.sub?.b?.label || "—"}{head.sub?.b?.amps ? <span style={{ color: C.muted }}> {head.sub.b.amps}A</span> : null}</span>
              <FlagTag flags={head.flags} />
            </>
          )}
          {head && head.type !== "tandem" && (
            <>
              <span style={{ fontSize: 13, fontWeight: head.type === "spare" ? 500 : 700, lineHeight: 1.3, color: head.type === "spare" ? C.muted : C.text, wordBreak: "break-word" }}>
                {head.type === "space" ? null : head.type === "blank" ? <span style={{ color: C.faint }}>—</span> : printedLabel(head) || <span style={{ color: C.faint }}>(no label)</span>}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>
                {head.amps ? `${head.amps}A` : ""}{head.poles > 1 ? ` ${head.poles}P` : ""}{head.type === "quad" ? " quad" : ""}
                {(head.tags || []).length ? ` · ${head.tags.join(" ").toUpperCase()}` : ""}
              </span>
              <FlagTag flags={head.flags} />
            </>
          )}
        </button>
        {head && onGrab && (
          <span role="button" aria-label={`Drag circuit ${head.number} to move or swap it`} title="Drag to move or swap" onPointerDown={(e) => onGrab(e, head)} style={{ ...NO_SELECT, position: "relative", width: 24, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "grab", color: C.muted, fontSize: 17, lineHeight: 1, borderRadius: 6, background: "rgba(255,255,255,.04)" }}>
            ⠿
            {/* Thumb target: the glyph stays 24 px (the label keeps its width on a
                phone) and an invisible pad reaches 18 px over the breaker-number
                column beside it — 42 px to press, the full height of the cell.
                The press bubbles to the handle, which owns the pointer capture.
                Off mid-drag so it never hides a slot from elementFromPoint. */}
            {thumb && <span aria-hidden="true" style={{ ...NO_SELECT, position: "absolute", top: -3, bottom: -3, left: parity ? 0 : -18, right: parity ? -18 : 0, pointerEvents: drag ? "none" : "auto" }} />}
          </span>
        )}
        </div>
      </td>
    );
  };

  const numCell = (n) => (
    <td key={`n${n}`} data-slot={n <= spaces ? n : undefined} style={{ width: 30, background: cand === n ? candCol : "#0a0c0f", color: cand === n ? "#10130a" : "#fff", textAlign: "center", fontSize: 12, fontWeight: 800, padding: 0, borderTop: `1px solid ${C.border}` }}>
      <div style={{ minHeight: 44, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", lineHeight: 1 }}>
        <span>{n}</span>
        <span style={{ fontSize: 8.5, color: C.faint, marginTop: 2 }}>{phaseOf(n, phases)}</span>
      </div>
    </td>
  );

  const body = [];
  for (let r = 0; r < rows; r++) {
    const odd = r * 2 + 1, even = r * 2 + 2;
    body.push(
      <tr key={r}>
        {cell(odd, 1)}
        {numCell(odd)}
        {numCell(even)}
        {even <= spaces ? cell(even, 0) : <td />}
      </tr>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}

// ── Bottom sheet editor for one row ─────────────────────────────────────────
function Sheet({ children, onClose }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { const t = setTimeout(() => setOpen(true), 10); return () => clearTimeout(t); }, []);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.62)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "14px 14px 0 0", padding: "14px 14px 28px", transform: open ? "translateY(0)" : "translateY(100%)", transition: "transform .22s ease-out" }}>
        {children}
      </div>
    </div>
  );
}
// ── Photo pane: the picture at 1× / 2× / 3× in a scrollable box ─────────────
// Shared by the pinned viewer above the grid and by the row sheet, so a row can
// be checked against the photo wherever the tech happens to be editing.
function PhotoPane({ url, name, height, right, onReload }) {
  const [zoom, setZoom] = useState(1);
  const [bad, setBad] = useState(null);
  const zoomBtn = (z) => (
    <button key={z} onClick={() => setZoom(z)} style={{ minWidth: 38, minHeight: 32, borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 800, border: `1px solid ${zoom === z ? C.lime : C.border}`, background: zoom === z ? "rgba(165,206,58,.16)" : C.surface, color: zoom === z ? C.lime : C.muted }}>{z}×</button>
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
        {[1, 2, 3].map(zoomBtn)}
        <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: C.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || ""}</span>
        {right}
      </div>
      {height !== 0 && (
        <div style={{ height, overflow: "auto", background: "#000", borderRadius: 8, WebkitOverflowScrolling: "touch" }}>
          {bad === url
            ? <div style={{ padding: 14, fontSize: 12.5, color: C.muted }}>The link to this photo has expired.{onReload ? <> <button onClick={onReload} style={{ background: "none", border: "none", color: C.lime, cursor: "pointer", padding: 0, fontSize: 12.5, fontWeight: 700 }}>Reload photos</button></> : null}</div>
            : <img src={url} alt={name || "Panel directory"} onError={() => setBad(url)} style={{ display: "block", width: `${zoom * 100}%`, maxWidth: "none" }} />}
        </div>
      )}
    </div>
  );
}
// The viewer pinned above the grid while rows are being reviewed. The app's
// content column is an overflow container that never scrolls (the window does),
// which defeats position:sticky — so the pin is done by hand: an in-flow holder
// keeps the space, and the box goes position:fixed under the mobile header for
// as long as the grid (containerRef) is still on screen below it.
function PhotoViewer({ photo, containerRef, onClose, onReload }) {
  const [open, setOpen] = useState(true);
  const [pin, setPin] = useState(null); // { top, left, width, height } while pinned
  const holder = useRef(null);
  const box = useRef(null);
  useEffect(() => {
    const measure = () => {
      const h = holder.current, b = box.current, c = containerRef.current;
      if (!h || !b || !c) return;
      const bar = document.querySelector(".mobile-header");
      const barRect = bar ? bar.getBoundingClientRect() : null;
      const top = barRect && barRect.height > 0 ? Math.max(0, barRect.bottom) : 0;
      const hr = h.getBoundingClientRect(), cr = c.getBoundingClientRect(), height = b.offsetHeight;
      const next = hr.top < top && cr.bottom > top + height + 90 ? { top, left: hr.left, width: hr.width, height } : null;
      setPin((p) => (JSON.stringify(p) === JSON.stringify(next) ? p : next));
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [containerRef, open, photo.url]);
  const small = { padding: "5px 10px", fontSize: 12, minHeight: 32 };
  return (
    <div ref={holder} style={{ marginTop: 12, minHeight: pin ? pin.height : undefined }}>
      <div ref={box} style={{ background: C.card, border: `1px solid ${C.lime}`, borderRadius: 12, padding: "8px 10px", boxSizing: "border-box", ...(pin ? { position: "fixed", top: pin.top, left: pin.left, width: pin.width, zIndex: 30, boxShadow: "0 10px 28px rgba(0,0,0,.6)", borderRadius: "0 0 12px 12px" } : {}) }}>
        <PhotoPane url={photo.url} name={photo.fileName || "Panel photo"} height={open ? "min(36vh, 320px)" : 0} onReload={onReload} right={
          <>
            <Btn onClick={() => setOpen(!open)} style={small}>{open ? "Hide" : "Show photo"}</Btn>
            <Btn onClick={onClose} style={small} title="Unpin the photo">×</Btn>
          </>
        } />
      </div>
    </div>
  );
}

function RowSheet({ number, circuit, schedule, spaces, photo, photoOpen, onChange, onAccept, onClose }) {
  const head = circuit || null;
  const [draft, setDraft] = useState(() => head ? { ...head, tags: [...(head.tags || [])], sub: head.sub ? { a: { ...head.sub.a }, b: { ...head.sub.b } } : null } : blankCircuit(number));
  const guessed = Array.isArray(head?.uncertain_fields) ? head.uncertain_fields : [];
  const uncertain = (head?.flags || []).includes("uncertain");
  // Opens with the photo on an uncertain row — unless the tech turned the photo
  // off for this review ("Show photo while reviewing" unticked, or the viewer closed).
  const [showPhoto, setShowPhoto] = useState(uncertain && photoOpen !== false);
  const guess = (f) => (uncertain && guessed.includes(f) ? { borderColor: C.orange } : null);
  const [menu, setMenu] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const [msg, setMsg] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const others = circuitsOf(schedule).filter((c) => c.number !== (head ? head.number : -1));

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setType = (type) => {
    const poles = TYPE_POLES[type] || 1;
    set({ type, poles, sub: type === "tandem" ? (draft.sub || { a: { amps: null, label: "" }, b: { amps: null, label: "" } }) : null, label: type === "spare" ? "Spare" : type === "space" || type === "blank" ? null : draft.label });
  };
  const commit = (next) => {
    const c = next || draft;
    const blocked = moveBlocker({ ...schedule, circuits: others }, c, c.number, spaces);
    if (blocked) { setMsg(blocked); return false; }
    onChange({ ...schedule, circuits: sortCircuits([...others, { ...c, flags: c.flags || [] }]) });
    return true;
  };
  const move = (to) => {
    const target = Number(to);
    const blocked = moveBlocker({ ...schedule, circuits: others }, draft, target, spaces);
    if (blocked) { setMsg(blocked); return; }
    setMenu(false);
    onChange({ ...schedule, circuits: sortCircuits([...others, { ...draft, number: target }]) });
    onClose();
  };
  const duplicate = () => {
    const at = lowestFree({ ...schedule, circuits: [...others, draft] }, spaces, slotsOf(draft).length);
    if (!at) { setMsg("No free slot big enough to duplicate into."); return; }
    onChange({ ...schedule, circuits: sortCircuits([...others, draft, { ...draft, number: at, raw: null, flags: [] }]) });
    onClose();
  };
  const clear = () => { onChange({ ...schedule, circuits: sortCircuits(others), removed: [...(schedule.removed || []), ...(head ? [head.number] : [])] }); onClose(); };
  const swapSide = () => move(draft.number % 2 === 1 ? draft.number + 1 : draft.number - 1);
  const flagged = (head?.flags || []).filter(isReviewFlag);
  // Accept takes what is on screen: a tech who corrects the amps and taps Accept
  // means "this row is now right", so the edit is committed first (the designer
  // then saves before it calls accept, so the server accepts the corrected row).
  const edited = !!head && JSON.stringify({ ...head, tags: head.tags || [], sub: head.sub || null }) !== JSON.stringify(draft);
  const acceptRow = async () => {
    if (edited && !commit()) return;
    setAccepting(true);
    await onAccept([draft.number]);
    setAccepting(false);
    onClose();
  };
  const menuBtn = { display: "block", width: "100%", textAlign: "left", padding: "10px 12px", minHeight: 40, background: "none", border: "none", borderBottom: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, cursor: "pointer" };

  return (
    <Sheet onClose={onClose}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>Circuit {draft.number}{draft.poles > 1 && draft.type !== "tandem" && draft.type !== "quad" ? `–${draft.number + 2 * (draft.poles - 1)}` : ""} <span style={{ fontSize: 11, color: C.faint, fontWeight: 600 }}>{phaseOf(draft.number, schedule.panel?.phases)} · {draft.number % 2 ? "left" : "right"}</span></div>
        <div style={{ display: "flex", gap: 6, position: "relative" }}>
          {head && <Btn onClick={() => setMenu(!menu)} style={{ padding: "6px 12px", fontSize: 16 }} title="More">⋮</Btn>}
          <Btn onClick={onClose} style={{ padding: "6px 10px", fontSize: 12 }}>Close</Btn>
          {menu && (
            <div style={{ position: "absolute", right: 0, top: 44, width: 230, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 5, boxShadow: "0 10px 30px rgba(0,0,0,.5)" }}>
              <button style={menuBtn} onClick={() => move(draft.number - 2)}>▲ Move up</button>
              <button style={menuBtn} onClick={() => move(draft.number + 2)}>▼ Move down</button>
              <button style={menuBtn} onClick={swapSide}>⇄ Swap side</button>
              <div style={{ display: "flex", gap: 6, padding: "8px 12px", borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
                <input type="number" inputMode="numeric" placeholder="#" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} style={{ ...inputStyle, width: 70, padding: "6px 8px" }} />
                <Btn onClick={() => moveTo && move(moveTo)} style={{ padding: "6px 10px", fontSize: 12 }}>Move to #</Btn>
              </div>
              <button style={menuBtn} onClick={duplicate}>⧉ Duplicate</button>
              <button style={{ ...menuBtn, color: C.red, borderBottom: "none" }} onClick={clear}>✕ Clear row</button>
            </div>
          )}
        </div>
      </div>

      {flagged.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "rgba(245,158,11,.10)", border: `1px solid ${C.orange}`, borderRadius: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: C.orange, fontWeight: 700 }}>Review: {flagged.map((f) => f.replace(/_/g, " ")).join(", ")}</span>
          <Btn disabled={accepting} onClick={acceptRow} style={{ padding: "6px 10px", fontSize: 12 }}>{accepting ? "…" : edited ? "Save & accept" : "Accept"}</Btn>
        </div>
      )}
      {/* A row read from a photo: what was actually seen, and which parts were a guess. */}
      {head && (uncertain || head.evidence) && (
        <div style={{ fontSize: 12, color: C.text, marginBottom: 8, lineHeight: 1.5, padding: "7px 10px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
          {head.evidence ? <div>Read as: <b style={{ fontFamily: "monospace" }}>{head.evidence}</b></div> : <div style={{ color: C.muted }}>Nothing legible was read for this row.</div>}
          {/* Only while the row is still uncertain: uncertain_fields stays on an accepted row as history, and "Guessed" there would read as an open problem. */}
          {uncertain && guessed.length > 0 && <div style={{ color: C.orange }}>Guessed: {guessed.map((f) => UNCERTAIN_FIELD_NAMES[f] || f).join(", ")} — check {guessed.length === 1 ? "it" : "them"} against the photo.</div>}
          {typeof head.confidence === "number" && head.confidence < 1 && uncertain && <div style={{ color: C.faint, fontSize: 11 }}>Confidence {Math.round(head.confidence * 100)}%</div>}
        </div>
      )}
      {photo && (
        <div style={{ marginBottom: 8 }}>
          {showPhoto
            ? <PhotoPane url={photo.url} name={photo.fileName || "Panel photo"} height="30vh" right={<Btn onClick={() => setShowPhoto(false)} style={{ padding: "5px 10px", fontSize: 12, minHeight: 32 }}>Hide photo</Btn>} />
            : <Btn onClick={() => setShowPhoto(true)} style={{ padding: "6px 10px", fontSize: 12, minHeight: 32 }}>Show the photo</Btn>}
        </div>
      )}
      {head?.raw && <div style={{ fontSize: 11, color: C.faint, marginBottom: 8, fontFamily: "monospace" }}>from: {head.raw}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="Type">
          <select value={draft.type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Poles">
          <input type="number" inputMode="numeric" min={1} max={3} value={draft.poles || 1} disabled={!["1p", "2p", "3p"].includes(draft.type)} onChange={(e) => { const p = Math.max(1, Math.min(3, Number(e.target.value) || 1)); set({ poles: p, type: p === 1 ? "1p" : p === 2 ? "2p" : "3p" }); }} style={{ ...inputStyle, ...guess("poles") }} />
        </Field>
        {draft.type !== "tandem" && draft.type !== "space" && draft.type !== "blank" && (
          <>
            <Field label="Amps">
              <input type="number" inputMode="numeric" value={draft.amps ?? ""} onChange={(e) => set({ amps: e.target.value === "" ? null : Number(e.target.value) })} style={{ ...inputStyle, ...guess("amps") }} />
            </Field>
            <Field label="Label (printed)">
              <input value={draft.label ?? ""} onChange={(e) => set({ label: e.target.value })} placeholder="Kitchen Counter Receptacles N" style={{ ...inputStyle, ...guess("label") }} />
            </Field>
            <Field label="Description (tech's words)" span>
              <input value={draft.description ?? ""} onChange={(e) => set({ description: e.target.value })} placeholder="kitchen counter plugs N" style={inputStyle} />
            </Field>
          </>
        )}
        {draft.type === "tandem" && (
          <>
            <Field label={`${draft.number}A amps`}><input type="number" inputMode="numeric" value={draft.sub?.a?.amps ?? ""} onChange={(e) => set({ sub: { ...draft.sub, a: { ...draft.sub.a, amps: e.target.value === "" ? null : Number(e.target.value) } } })} style={inputStyle} /></Field>
            <Field label={`${draft.number}A label`}><input value={draft.sub?.a?.label ?? ""} onChange={(e) => set({ sub: { ...draft.sub, a: { ...draft.sub.a, label: e.target.value } } })} style={inputStyle} /></Field>
            <Field label={`${draft.number}B amps`}><input type="number" inputMode="numeric" value={draft.sub?.b?.amps ?? ""} onChange={(e) => set({ sub: { ...draft.sub, b: { ...draft.sub.b, amps: e.target.value === "" ? null : Number(e.target.value) } } })} style={inputStyle} /></Field>
            <Field label={`${draft.number}B label`}><input value={draft.sub?.b?.label ?? ""} onChange={(e) => set({ sub: { ...draft.sub, b: { ...draft.sub.b, label: e.target.value } } })} style={inputStyle} /></Field>
          </>
        )}
        <Field label="Tags" span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TAGS.map((t) => {
              const on = (draft.tags || []).includes(t);
              return <Chip key={t} on={on} onClick={() => set({ tags: on ? draft.tags.filter((x) => x !== t) : [...(draft.tags || []), t] })}>{t.toUpperCase()}</Chip>;
            })}
          </div>
        </Field>
      </div>

      {msg && <Banner kind="warn" text={msg} onClose={() => setMsg(null)} />}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <Btn kind="pri" onClick={() => { if (commit()) onClose(); }}>{head ? "Save row" : "Add circuit"}</Btn>
        {head && <Btn kind="danger" onClick={clear}>Clear</Btn>}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: C.faint, alignSelf: "center" }}>row {Math.ceil(draft.number / 2)}</span>
      </div>
    </Sheet>
  );
}

// ── Job / request linker (Jobber search, SavePanel idiom) ───────────────────
function JobLinker({ initialQ, onPick, onClose }) {
  const [mode, setMode] = useState("job");
  const [q, setQ] = useState(initialQ || "");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr(null); setRows(null);
    try {
      const r = await apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: mode === "job" ? "searchJobs" : "searchRequests", searchTerm: q.trim() }) });
      const d = await r.json();
      if (!d.ok && d.error) { setErr(`Jobber search failed: ${d.error}`); setRows([]); return; }
      setRows(mode === "job" ? (d.jobs || []) : (d.requests || []));
    } catch (e) { setErr(`Jobber search failed: ${e.message}`); setRows([]); }
    finally { setBusy(false); }
  };
  const pickJob = (j) => onPick({ jobber_job_id: j.id, job_number: String(j.jobNumber || ""), job_title: j.title || "", client_name: j.clientName || j.client?.name || null, jobber_client_id: j.client?.id || null });
  const pickReq = (rq) => {
    const job = (rq.jobs?.nodes || [])[0] || null;
    const a = rq.property?.address;
    onPick({ jobber_request_id: rq.id, request_title: rq.title || "", client_name: rq.client?.name || null, jobber_client_id: rq.client?.id || null, site_address: a ? [a.street1, a.city, a.province].filter(Boolean).join(", ") : null, jobber_job_id: job?.id || null, job_number: job ? String(job.jobNumber || "") : null, job_title: job?.title || null });
  };
  return (
    <Sheet onClose={onClose}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>Link to Jobber</div>
        <Btn onClick={onClose} style={{ padding: "6px 10px", fontSize: 12 }}>Cancel</Btn>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <Chip on={mode === "job"} onClick={() => { setMode("job"); setRows(null); }}>Job</Chip>
        <Chip on={mode === "request"} onClick={() => { setMode("request"); setRows(null); }}>Request</Chip>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} autoFocus placeholder="Client, title, or job number…" style={inputStyle} />
        <Btn kind="pri" disabled={busy} onClick={run}>{busy ? "…" : "Search"}</Btn>
      </div>
      {err && <Banner kind="err" text={err} />}
      <div style={{ display: "grid", gap: 7 }}>
        {rows && !rows.length && <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>Nothing matched.</div>}
        {(rows || []).map((r) => (
          <button key={r.id} onClick={() => (mode === "job" ? pickJob(r) : pickReq(r))} style={{ textAlign: "left", cursor: "pointer", padding: "9px 11px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, minHeight: 44 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.clientName || r.client?.name || "(no client)"}</span>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: C.faint, whiteSpace: "nowrap" }}>{mode === "job" ? `#${r.jobNumber}` : r.requestStatus || "request"}</span>
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title || "(no title)"}</div>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

// ── Photo intake: list, upload, read (contract §11.4 photos / upload-photo / parse-photo) ─
// One instance lives in the Designer and feeds both the photo card and the
// parse box. `job` is the one upload/read in progress: { origin:"card"|"box",
// stage:"upload"|"read"|"done"|"error", photo, parsed, error }.
function usePhotoIntake(scheduleId) {
  const [photos, setPhotos] = useState(null); // null = not loaded yet
  const [info, setInfo] = useState({ loading: false, error: null, liveError: null, truncated: false });
  const [selKey, setSelKey] = useState(null);
  const [job, setJob] = useState(null);
  const working = useRef(false);

  const refresh = useCallback(async () => {
    setInfo((i) => ({ ...i, loading: true, error: null }));
    const r = await api("photos", { id: scheduleId });
    if (!r.ok) { setInfo({ loading: false, error: r.error || "Couldn't load the photos", liveError: null, truncated: false }); setPhotos((p) => p || []); return; }
    setPhotos(Array.isArray(r.photos) ? r.photos.filter((p) => p && p.key) : []);
    setInfo({ loading: false, error: null, liveError: r.liveError || null, truncated: !!r.truncated });
  }, [scheduleId]);
  useEffect(() => { refresh(); }, [refresh]);

  const read = useCallback(async (photo, origin) => {
    if (working.current || !photo?.key) return;
    working.current = true;
    setJob({ origin, stage: "read", photo });
    const r = await api("parse-photo", { id: scheduleId, key: photo.key });
    working.current = false;
    if (!r.ok || !r.schedule) { setJob({ origin, stage: "error", photo, error: r.error || "Couldn't read that photo" }); return; }
    setJob({ origin, stage: "done", photo, parsed: r });
  }, [scheduleId]);

  // Downscale in the browser, upload, select it. From the parse box the read
  // follows straight away; from the photo card the tech presses "Read this photo".
  const add = useCallback(async (file, origin) => {
    if (working.current) return;
    if (!file || !/^image\//.test(file.type || "")) { setJob({ origin, stage: "error", error: "That isn't a picture — use a JPEG or PNG photo of the panel or its directory card." }); return; }
    working.current = true;
    setJob({ origin, stage: "upload" });
    let photo = null;
    try {
      const { blob, contentType } = await preparePhoto(file);
      const ext = contentType === "image/png" ? ".png" : contentType === "image/webp" ? ".webp" : ".jpg";
      const fileName = `${String(file.name || "panel-photo").replace(/\.[^.]*$/, "").slice(0, 60) || "panel-photo"}${ext}`;
      const r = await api("upload-photo", { id: scheduleId, dataBase64: await blobToBase64(blob), contentType, fileName });
      if (!r.ok || !r.photo?.key) throw new Error(r.error || "Couldn't upload that photo");
      photo = { source: "upload", takenAt: new Date().toISOString(), ...r.photo };
    } catch (e) {
      working.current = false;
      setJob({ origin, stage: "error", error: String(e?.message || e) });
      return;
    }
    working.current = false;
    setPhotos((p) => [photo, ...(p || []).filter((x) => x.key !== photo.key)]);
    setSelKey(photo.key);
    if (!photo.url) refresh(); // stored but not signed yet — the listing signs it
    if (origin === "box") await read(photo, origin); else setJob(null);
  }, [scheduleId, read, refresh]);

  const busy = !!job && (job.stage === "upload" || job.stage === "read");
  return { photos, info, selKey, setSelKey, job, busy, refresh, read, add, clear: () => setJob(null) };
}
function photoCounts(parsed) {
  const ps = normaliseSchedule(parsed.schedule);
  const uncertain = ps.circuits.filter((c) => (c.flags || []).includes("uncertain")).length;
  return { n: ps.circuits.length, uncertain, other: reviewRows(ps).length - uncertain, unplaced: ps.unplaced.length };
}
function photoSummary(parsed) {
  const { n, uncertain, other, unplaced } = photoCounts(parsed);
  return `${n} circuit${n === 1 ? "" : "s"} read · ${uncertain} uncertain${other > 0 ? ` · ${other} more to review` : ""} · ${unplaced} not placed`;
}
// Why a finished photo read can't be applied (null = it can).
function photoProblem(parsed) {
  if (parsed.error === "no_api_key") return "Photo reading isn't switched on for this server (no AI key).";
  if (parsed.photoKind === "not_a_panel") return "That doesn't look like a panel or a directory card — nothing was read.";
  if (!circuitsOf(parsed.schedule).length) return parsed.error ? `Nothing was read — ${parsed.error}` : "No circuits could be read from that photo.";
  return null;
}
// The file drop zone — the ONLY place HTML5 drag events are used (they carry
// files from the desktop; they never fire for touch, which is why circuits and
// labels move with the pointer engine instead).
function useFileDrop(onFile) {
  const [over, setOver] = useState(false);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const props = {
    onDragOver: (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; if (!over) setOver(true); },
    onDragLeave: () => setOver(false),
    onDrop: (e) => { if (!hasFiles(e)) return; e.preventDefault(); setOver(false); onFile(firstImageFile(e.dataTransfer.files)); },
  };
  return [over, props];
}
// Summary + Apply buttons, shared by the pasted-text result and the photo result.
function ParsedResult({ summary, unplaced, hints, problem, extra, canMerge, onApply, onDiscard }) {
  return (
    <div style={{ marginTop: 10, padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{problem || summary}</div>
      {!problem && (unplaced || []).length > 0 && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 4 }}>Unplaced: {unplaced.map((u) => `"${u.raw}" (${u.reason || "no number"})`).join("; ")}</div>}
      {!problem && (hints || []).length > 0 && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{hints.join(" · ")}</div>}
      {!problem && extra}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {!problem && <Btn kind="pri" onClick={() => onApply("replace")}>Apply — replace</Btn>}
        {!problem && <Btn onClick={() => onApply("merge")} disabled={!canMerge}>Apply — merge into free slots</Btn>}
        <Btn onClick={onDiscard}>{problem ? "OK" : "Discard"}</Btn>
      </div>
    </div>
  );
}
// Status / result of a photo job, shown wherever the job was started.
function PhotoJob({ job, canMerge, onApply, onDiscard }) {
  if (!job) return null;
  if (job.stage === "upload") return <div style={{ fontSize: 12.5, color: C.blue, marginTop: 8 }}>Uploading the photo…</div>;
  if (job.stage === "read") return <div style={{ fontSize: 12.5, color: C.blue, marginTop: 8 }}>Reading the photo — this takes up to a minute…</div>;
  if (job.stage === "error") return <Banner kind="err" text={job.error} onClose={onDiscard} />;
  return <PhotoResult parsed={job.parsed} canMerge={canMerge} onApply={onApply} onDiscard={onDiscard} />;
}
// A finished photo read: the counts, how many rows came back uncertain (those are
// the ones to check against the picture), and whether to keep the picture pinned
// above the grid while they are reviewed — ticked by default, mounted fresh per read.
function PhotoResult({ parsed, canMerge, onApply, onDiscard }) {
  const [showPhoto, setShowPhoto] = useState(true);
  const { n, uncertain } = photoCounts(parsed);
  const extra = (
    <>
      <div style={{ fontSize: 12, color: uncertain ? C.orange : C.muted, marginTop: 6, lineHeight: 1.5 }}>
        {uncertain
          ? `${uncertain} of the ${n} row${n === 1 ? "" : "s"} ${uncertain === 1 ? "is" : "are"} uncertain — ${uncertain === 1 ? "it comes" : "they come"} in amber for you to check against the photo and Accept, one by one.`
          : "No row came back uncertain."}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 9, minHeight: 40, marginTop: 2, fontSize: 12.5, color: C.text, cursor: "pointer" }}>
        <input type="checkbox" checked={showPhoto} onChange={(e) => setShowPhoto(e.target.checked)} style={{ width: 20, height: 20, margin: 0, accentColor: C.lime, flexShrink: 0 }} />
        <span>Show photo while reviewing</span>
      </label>
    </>
  );
  return <ParsedResult summary={photoSummary(parsed)} unplaced={normaliseSchedule(parsed.schedule).unplaced} problem={photoProblem(parsed)} extra={extra} canMerge={canMerge} onApply={(mode) => onApply(mode, { showPhoto })} onDiscard={onDiscard} />;
}

// ── Photo card: pick a job photo (or add one) and read it ───────────────────
function PhotoCard({ intake, jobLinked, canMerge, onApply }) {
  const { photos, info, selKey, setSelKey, job, busy } = intake;
  const camera = useRef(null);
  const library = useRef(null);
  const [over, drop] = useFileDrop((f) => intake.add(f, "card"));
  const selected = (photos || []).find((p) => p.key === selKey) || null;
  const mine = job && job.origin === "card" ? job : null;
  const onPick = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) intake.add(f, "card"); };
  const when = (t) => { const d = t ? new Date(t) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : ""; };
  return (
    <Card title="Read a panel photo" right={<Btn disabled={info.loading} onClick={intake.refresh} style={{ padding: "5px 10px", fontSize: 12, minHeight: 32 }}>{info.loading ? "Loading…" : "↻ Refresh"}</Btn>}>
      <div {...drop} style={{ border: `1.5px dashed ${over ? C.lime : C.border}`, background: over ? "rgba(165,206,58,.08)" : "transparent", borderRadius: 10, padding: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input ref={camera} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onPick} />
          <input ref={library} type="file" accept="image/*" style={{ display: "none" }} onChange={onPick} />
          <Btn kind="pri" disabled={busy} onClick={() => camera.current && camera.current.click()}>Take a photo</Btn>
          <Btn disabled={busy} onClick={() => library.current && library.current.click()}>Upload a photo</Btn>
          <span style={{ fontSize: 11, color: C.faint, flex: 1, minWidth: 140 }}>…or drop a picture here. Photos on the Jobber job show up below.</span>
        </div>
        {info.error && <Banner kind="err" text={info.error} />}
        {info.liveError && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 6 }}>Couldn't reach Jobber for the newest photos ({String(info.liveError)}) — showing the ones already in ElecBid.</div>}
        {photos === null && !info.error && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>Loading photos…</div>}
        {photos && !photos.length && !info.error && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>{jobLinked ? "No photos on this job yet. Take one of the panel or the directory card." : "No job linked, so only photos added here are listed. Take one of the panel or the directory card."}</div>}
        {photos && photos.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 6, marginTop: 10 }}>
            {photos.map((p) => (
              <button key={p.key} disabled={busy} onClick={() => setSelKey(p.key === selKey ? null : p.key)} title={[p.fileName, p.caption, p.author, when(p.takenAt)].filter(Boolean).join(" · ")} style={{ position: "relative", height: 84, padding: 0, borderRadius: 8, overflow: "hidden", cursor: busy ? "default" : "pointer", background: "#000", border: `2px solid ${p.key === selKey ? C.lime : C.border}` }}>
                {(p.thumbUrl || p.url) && <img src={p.thumbUrl || p.url} alt={p.fileName || "Job attachment"} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
                <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, fontSize: 9.5, fontWeight: 800, color: "#fff", background: "rgba(0,0,0,.66)", padding: "2px 3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{PHOTO_SOURCE_NAMES[p.source] || p.source || ""}</span>
              </button>
            ))}
          </div>
        )}
        {info.truncated && <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>Showing the newest 60 photos.</div>}
      </div>
      {selected && (
        <div style={{ marginTop: 10 }}>
          {(selected.url || selected.thumbUrl) && <img src={selected.url || selected.thumbUrl} alt={selected.fileName || "Selected job attachment"} style={{ display: "block", width: "100%", maxHeight: "52vh", objectFit: "contain", background: "#000", borderRadius: 10 }} />}
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5 }}>{[PHOTO_SOURCE_NAMES[selected.source] || selected.source, selected.fileName, when(selected.takenAt), selected.author, selected.caption].filter(Boolean).join(" · ")}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Btn kind="pri" disabled={busy} onClick={() => intake.read(selected, "card")}>{busy && mine ? "Reading…" : "Read this photo"}</Btn>
            <span style={{ fontSize: 11, color: C.faint, flex: 1, minWidth: 160 }}>Anything it isn't sure of comes back amber for you to confirm row by row.</span>
          </div>
        </div>
      )}
      <PhotoJob job={mine} canMerge={canMerge} onApply={onApply} onDiscard={intake.clear} />
    </Card>
  );
}

// ── Standard label palette (contract §11.5 item 4) ──────────────────────────
// Two ways to place a label, both through the same placeLabel(): drag the chip
// onto a slot (pointer engine), or tap it to arm it and then tap a slot.
function LabelPalette({ org, armed, onArm, onGrab }) {
  const [open, setOpen] = useState(true);
  const [group, setGroup] = useState(LABEL_GROUPS[0][0]);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(() => loadMyLabels(org));
  const [draft, setDraft] = useState("");
  const [draft2p, setDraft2p] = useState(false);
  const [msg, setMsg] = useState(null);
  const keep = (list) => { setMine(list); saveMyLabels(org, list); };
  const addMine = () => {
    const label = draft.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!label) return;
    if ([...mine, ...STANDARD_LABELS].some((x) => x.label.toLowerCase() === label.toLowerCase())) { setMsg(`"${label}" is already in the list.`); return; }
    keep([...mine, { group: MY_GROUP, label, ...(draft2p ? { poles: 2 } : {}) }].slice(-80));
    setDraft(""); setDraft2p(false); setMsg(null);
  };
  const needle = q.trim().toLowerCase();
  const shown = needle ? [...mine, ...STANDARD_LABELS].filter((x) => x.label.toLowerCase().includes(needle)).slice(0, 48)
    : group === MY_GROUP ? mine : STANDARD_LABELS.filter((x) => x.group === group);
  const tab = (name) => (
    <button key={name} onClick={() => { setGroup(name); setQ(""); }} style={{ flexShrink: 0, padding: "6px 11px", minHeight: 34, borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", border: `1px solid ${!needle && group === name ? C.lime : C.border}`, background: !needle && group === name ? "rgba(165,206,58,.16)" : "transparent", color: !needle && group === name ? C.lime : C.muted }}>{name}{name === MY_GROUP && mine.length ? ` (${mine.length})` : ""}</button>
  );
  return (
    <Card title="Labels" right={<Btn onClick={() => setOpen(!open)} style={{ padding: "5px 10px", fontSize: 12, minHeight: 32 }}>{open ? "Hide" : "Show"}</Btn>}>
      {open && (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search labels…" style={{ ...inputStyle, marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", scrollbarColor: `${C.border} transparent` }}>
            {[MY_GROUP, ...LABEL_GROUPS.map((g) => g[0])].map(tab)}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {!shown.length && <span style={{ fontSize: 12.5, color: C.muted, padding: "6px 0" }}>{needle ? "No label matches — add it under My labels." : "No labels of your own yet — add one below."}</span>}
            {shown.map((item) => {
              const on = !!armed && armed.label === item.label;
              const custom = item.group === MY_GROUP;
              return (
                <span key={`${item.group}:${item.label}`} style={{ display: "inline-flex", alignItems: "stretch" }}>
                  <button onPointerDown={(e) => onGrab(e, item)} onClick={(e) => onArm(item, e.currentTarget)} title="Drag onto a slot, or tap then tap a slot" style={{ ...NO_SELECT, padding: "8px 11px", minHeight: 40, cursor: "grab", fontSize: 12.5, fontWeight: 700, borderRadius: custom ? "999px 0 0 999px" : 999, border: `1px solid ${on ? C.lime : C.border}`, background: on ? C.lime : C.surface, color: on ? "#10130a" : C.text }}>
                    {item.label}{item.poles === 2 ? <span style={{ fontSize: 10, fontWeight: 800, opacity: 0.7 }}> 2P</span> : null}
                  </button>
                  {custom && <button onClick={() => { keep(mine.filter((x) => x.label !== item.label)); if (on) onArm(item); }} aria-label={`Remove ${item.label} from my labels`} title="Remove from my labels" style={{ minWidth: 32, cursor: "pointer", fontSize: 14, fontWeight: 800, borderRadius: "0 999px 999px 0", border: `1px solid ${C.border}`, borderLeft: "none", background: C.surface, color: C.faint }}>×</button>}
                </span>
              );
            })}
          </div>
          {(group === MY_GROUP || needle) && (
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMine()} maxLength={40} placeholder="Add your own label…" style={{ ...inputStyle, flex: 1, minWidth: 150, width: "auto" }} />
              <Chip on={draft2p} onClick={() => setDraft2p(!draft2p)}>2-pole</Chip>
              <Btn onClick={addMine} disabled={!draft.trim()}>Add</Btn>
            </div>
          )}
          {msg && <Banner kind="warn" text={msg} onClose={() => setMsg(null)} />}
          <div style={{ fontSize: 11, color: C.faint, marginTop: 8, lineHeight: 1.5 }}>Drag a label onto a slot, or tap it and then tap the slot. On a named circuit it only changes the words — a label never sets the breaker size.</div>
        </>
      )}
    </Card>
  );
}

// ── Parse box ("the chat box"): typed / pasted text, or a photo ─────────────
function PasteBox({ scheduleId, schedule, spaces, onApply, intake, onApplyPhoto }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [err, setErr] = useState(null);
  const picker = useRef(null);
  const addPhoto = (f) => { if (intake) intake.add(f, "box"); };
  const [over, drop] = useFileDrop(addPhoto);
  const photoJob = intake && intake.job && intake.job.origin === "box" ? intake.job : null;
  const photoBusy = !!intake && intake.busy;
  // A pasted screenshot / camera image is read as a photo. A paste that ALSO
  // carries plain text (Excel and Word put both on the clipboard) stays text.
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const img = items.find((i) => i.kind === "file" && /^image\//.test(i.type || ""));
    if (!img || !intake || items.some((i) => i.kind === "string" && i.type === "text/plain")) return;
    const f = img.getAsFile();
    if (!f) return;
    e.preventDefault();
    addPhoto(f);
  };
  const parse = async () => {
    if (!text.trim()) return;
    setBusy(true); setErr(null); setParsed(null);
    const r = await api("parse", { text, id: scheduleId });
    setBusy(false);
    if (!r.ok) { setErr(r.error || "Couldn't parse that"); return; }
    setParsed(r);
  };
  const ps = parsed ? normaliseSchedule(parsed.schedule) : null;
  const summary = ps ? `${ps.circuits.length} circuit${ps.circuits.length === 1 ? "" : "s"}, ${reviewRows(ps).length} to review, ${ps.unplaced.length} unplaced${parsed.usedClaude ? " · Claude helped with the prose" : ""}` : "";
  const apply = (mode) => {
    if (mode === "replace" && circuitsOf(schedule).length && !window.confirm(`Replace all ${circuitsOf(schedule).length} circuits on this schedule with the ${ps.circuits.length} parsed ones?`)) return;
    const { next, skipped } = applyParsed(schedule, ps, mode, spaces, null);
    onApply(next, skipped);
    setParsed(null); setText("");
  };
  return (
    <Card title="Paste a circuit list or drop a photo">
      <div {...drop} style={{ borderRadius: 10, outline: over ? `2px dashed ${C.lime}` : "none", outlineOffset: 3, background: over ? "rgba(165,206,58,.08)" : "transparent" }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onPaste={onPaste} rows={4} placeholder={"1 kitchen counter plugs N 20 gfci\n2-4 range 40/2\n5a compressor 20 / 5b exhaust fan 15\nrest spare\n\n…or paste / drop a photo of the panel"} style={{ ...inputStyle, resize: "vertical", fontFamily: "monospace", fontSize: 13 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Btn kind="pri" disabled={busy || !text.trim()} onClick={parse}>{busy ? "Parsing…" : "Parse"}</Btn>
          {intake && (
            <>
              <input ref={picker} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) addPhoto(f); }} />
              <Btn disabled={photoBusy} onClick={() => picker.current && picker.current.click()} title="Read a photo of the panel or its directory card">📷 Photo</Btn>
            </>
          )}
          <span style={{ fontSize: 11, color: C.faint, flex: 1, minWidth: 160 }}>Same grammar as the /panel note: "3-5 dryer 30/2", "7 lites 15", "rest spare". A dropped or pasted picture is read as a panel photo.</span>
        </div>
      </div>
      {err && <Banner kind="err" text={err} onClose={() => setErr(null)} />}
      {ps && <ParsedResult summary={summary} unplaced={ps.unplaced} hints={parsed.hints} canMerge={circuitsOf(schedule).length > 0} onApply={apply} onDiscard={() => setParsed(null)} />}
      {intake && <PhotoJob job={photoJob} canMerge={circuitsOf(schedule).length > 0} onApply={onApplyPhoto} onDiscard={intake.clear} />}
    </Card>
  );
}

// ── The designer for one schedule ───────────────────────────────────────────
function Designer({ scheduleId, onExit }) {
  const [row, setRow] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [size, setSize] = useState({ preset: "ql62_universal", custom_mm: null, auto_length: true, length_mm: null });
  const [saveState, setSaveState] = useState("idle");
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [tap, setTap] = useState(null);      // circuit number the sheet is open on
  const [linking, setLinking] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [showHeader, setShowHeader] = useState(false);
  // v1.1: company (name / phone / logo) is kept apart from `row` because a save
  // response carries the row without it; the armed palette label; the toast that
  // reports a drop; the photo pinned above the grid.
  const [company, setCompany] = useState(null);
  const [armed, setArmed] = useState(null);
  const [toast, setToast] = useState(null);       // { kind:"warn"|"info", text }
  const [pinned, setPinned] = useState(null);     // { key, url, fileName }
  const [viewerClosed, setViewerClosed] = useState(false);
  // Review state: `synced` is the exact schedule object the server last handed
  // back (get / save / accept). While it is the one on screen, row.needs_review
  // is the answer; only a document that is AHEAD of the server is judged locally.
  const [synced, setSynced] = useState(null);
  const [saveErr, setSaveErr] = useState(null);   // why the server refused the last save — the "Couldn't save" banner
  const [held, setHeld] = useState(null);         // an export / push / close stopped by a failed save: { what, offline, text, draft }
  const reviewRef = useRef(null);
  const intake = usePhotoIntake(scheduleId);

  // Save discipline (see GeneratorReport): local state is the only writer, one
  // save in flight, a dirty flag re-runs, and a response only replaces local
  // state when nothing changed while it was travelling (editSeq unchanged).
  const docRef = useRef({ schedule: null, size: null });
  const editSeq = useRef(0);
  const inFlight = useRef(false);
  const dirty = useRef(false);      // an edit landed while a save was in flight → re-save after
  const unsaved = useRef(false);    // any edit the server hasn't confirmed yet (flush waits on it)
  const saveFail = useRef(null);    // why the last save failed: { offline, text } (null = it landed)
  const timer = useRef(null);
  const [exportUrl, setExportUrl] = useState(null);
  useEffect(() => { docRef.current = { schedule, size }; }, [schedule, size]);

  const load = useCallback(async () => {
    const r = await api("get", { id: scheduleId });
    if (!r.ok) { setErr(r.error || "Couldn't load this schedule"); return; }
    setRow(r.schedule);
    setCompany(r.company || r.schedule.company || null);
    let sch = normaliseSchedule(r.schedule.schedule);
    let sz = { preset: "ql62_universal", custom_mm: null, auto_length: true, length_mm: null, ...(r.schedule.size || {}) };
    let fromServer = true;
    try {
      const raw = localStorage.getItem(LS_KEY(scheduleId));
      if (raw) {
        const local = JSON.parse(raw);
        const serverAt = new Date(r.schedule.updated_at || 0).getTime();
        if (local?.savedAt && local.savedAt > serverAt && local.schedule) {
          sch = normaliseSchedule(local.schedule);
          if (local.size) sz = { ...sz, ...local.size };
          fromServer = false;
          setSaveState("offline");
          dirty.current = true;
          unsaved.current = true;
        }
      }
    } catch { /* a corrupt draft is not worth failing over */ }
    setSchedule(sch); setSize(sz);
    setSynced(fromServer ? sch : null); // a restored phone draft is ahead of the server until its save lands
  }, [scheduleId]);
  useEffect(() => { load(); }, [load]);

  const pushSave = useCallback(async () => {
    if (inFlight.current) { dirty.current = true; return; }
    const { schedule: sch, size: sz } = docRef.current;
    if (!sch) return;
    inFlight.current = true;
    const seq = editSeq.current;
    setSaveState("saving");
    const res = resolveSizeLocal(sch, sz);
    const sizeOut = { preset: res.preset, custom_mm: exportCustomMm(res, sz), length_mm: Math.round(res.length_mm), width_mm: res.width_mm, rows: res.rows, strips: res.strips.length, auto_length: sz.auto_length !== false };
    const r = await api("save", { id: scheduleId, patch: { schedule: sch, size: sizeOut, panel_name: sch.panel?.name || "Panel A" } });
    inFlight.current = false;
    saveFail.current = r.ok ? null : { offline: !!r.offline, text: r.offline ? "no connection" : String(r.error || "the save failed") };
    if (r.ok) {
      setRow(r.schedule);
      setSaveErr(null);
      // Adopt the server's re-validated copy (flags, derived spaces) only if the
      // tech hasn't typed since — otherwise the dirty re-save carries it anyway.
      // The phone draft goes only then too: an edit made mid-flight wrote a NEWER
      // draft that this save did not carry, and it must survive until its own save.
      const current = editSeq.current === seq;
      if (current) {
        const adopted = r.schedule?.schedule ? normaliseSchedule(r.schedule.schedule) : sch;
        setSchedule(adopted);
        setSynced(adopted);
        unsaved.current = false;
        setHeld(null);
        try { localStorage.removeItem(LS_KEY(scheduleId)); } catch { /* private mode */ }
      }
      setSaveState(current ? "saved" : "dirty");
    } else {
      setSaveState(r.offline ? "offline" : "dirty");
      if (!r.offline) setSaveErr(saveFail.current.text);
    }
    if (dirty.current) { dirty.current = false; pushSave(); }
    return !!r.ok;
  }, [scheduleId]);

  // Once the load has restored a newer phone draft, flush it to the server.
  useEffect(() => { if (schedule && saveState === "offline" && dirty.current) { dirty.current = false; pushSave(); } }, [schedule, saveState, pushSave]);

  const change = (nextSchedule, nextSize) => {
    editSeq.current++;
    const sch = nextSchedule || docRef.current.schedule;
    const sz = nextSize || docRef.current.size;
    if (nextSchedule) setSchedule(sch);
    if (nextSize) setSize(sz);
    // Current at once (not only after the next render): accept / export flush a
    // save in the same tick as the edit that precedes them.
    docRef.current = { schedule: sch, size: sz };
    unsaved.current = true;
    try { localStorage.setItem(LS_KEY(scheduleId), JSON.stringify({ schedule: sch, size: sz, savedAt: Date.now() })); } catch { /* ignore */ }
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(pushSave, SAVE_DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const setPanel = (patch) => change({ ...schedule, panel: { ...schedule.panel, ...patch } });

  const patchRow = async (patch) => {
    const r = await api("save", { id: scheduleId, patch });
    if (!r.ok) { setErr(r.error || "Couldn't update"); return false; }
    setRow(r.schedule);
    return true;
  };
  // Export / push / accept / close must never run one edit behind: wait for any
  // save in flight, then keep saving until the server has confirmed the latest
  // edit. → null once it has; otherwise WHY it hasn't: { offline, text, draft }
  // (draft = the edits are at least safe in this phone's localStorage).
  const flush = async () => {
    clearTimeout(timer.current);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    for (let i = 0; i < 400 && (inFlight.current || dirty.current || unsaved.current); i++) {
      if (inFlight.current) { await sleep(50); continue; }
      const ok = await pushSave();
      if (ok === false) break;
    }
    if (!(inFlight.current || dirty.current || unsaved.current)) return null;
    let draft = false;
    try { draft = !!localStorage.getItem(LS_KEY(scheduleId)); } catch { /* private mode — no draft */ }
    return { ...(saveFail.current || { offline: false, text: "the save is taking too long" }), draft };
  };
  // Run fn once the latest edit is on the server. A save that did NOT land never
  // lets it through silently: the action stops and `held` puts the reason next
  // to the button with a Retry. The one way past is the tech's own choice —
  // when the failure was a dead network and the edits are safe in the phone
  // draft, the prompt says plainly that the server only has the last saved copy
  // and offers to go ahead with that (lastSaved = they said yes; it skips the
  // save). Closing in that same state needs no prompt: it is what the draft is
  // for, and it re-syncs the next time this schedule opens.
  const afterSave = async (what, fn, lastSaved) => {
    setHeld(null);
    if (!lastSaved) {
      const failed = await flush();
      if (failed && !(what === "close" && failed.offline && failed.draft)) { setHeld({ what, ...failed }); return null; }
    }
    return fn();
  };
  // Accept works on the SERVER's copy, so the latest edit (a photo just applied,
  // a row just corrected) is saved first — otherwise it would accept stale rows,
  // and adopting its response would throw the unsaved edits away (and move
  // updated_at past the phone draft, so a reload would discard the draft too).
  // If that save doesn't land, nothing is accepted: the edits stay on screen and
  // in localStorage; the tech just taps Accept again.
  const accept = async (numbers) => {
    const failed = await flush();
    if (failed) {
      setErr(`Couldn't save your latest edits (${failed.text}) — nothing was accepted. Your edits are still here; try again.`);
      return;
    }
    const seq = editSeq.current;
    const r = await api("accept", { id: scheduleId, numbers });
    if (!r.ok) { setErr(r.error || "Couldn't accept"); return; }
    setRow(r.schedule);
    if (!r.schedule?.schedule) return;
    const server = normaliseSchedule(r.schedule.schedule);
    // Same guard as pushSave: the server's copy replaces the screen only if the
    // tech hasn't edited while accept was in flight. If they have, their edit
    // stays and the acceptance is replayed onto it, so the save that follows
    // carries both (it would otherwise send the rows back still flagged).
    if (editSeq.current === seq) { setSchedule(server); setSynced(server); }
    else if (docRef.current.schedule) change(acceptLocally(docRef.current.schedule, server, numbers));
  };
  // The server's own refusal, in words (export / push answer 409 needs_review).
  const refusal = (r, fallback) => (r.error === "needs_review"
    ? `The saved copy still has rows marked for review${Array.isArray(r.flagged) && r.flagged.length ? ` (#${r.flagged.join(", #")})` : ""} — accept them first.`
    : r.error || fallback);

  // ── v1.1: drag engine, armed-label taps, photo apply ──────────────────────
  // Drops are judged against docRef (always the latest document), by the same
  // pure function that coloured the candidate slot.
  const slotDrag = useSlotDrag({
    check: (payload, slot) => dropResult(docRef.current.schedule, payload, slot).error || null,
    onDrop: (payload, slot) => {
      const r = dropResult(docRef.current.schedule, payload, slot);
      if (r.error) { setToast({ kind: "warn", text: r.error }); return; }
      if (!r.next) return;
      change(r.next);
      setToast({ kind: "info", text: r.note || "Done." });
    },
  });
  const grabLabel = (e, item) => slotDrag.begin(e, { kind: "label", label: item.label, poles: item.poles }, item.label);
  const grabCircuit = (e, head) => slotDrag.begin(e, { kind: "circuit", number: head.number }, `${head.number} · ${printedLabel(head) || (head.type === "tandem" ? "tandem" : head.type === "space" ? "space" : "unnamed")}`);
  const arm = (item, el) => { if (el && slotDrag.justEnded(el)) return; setArmed((cur) => (cur && cur.label === item.label ? null : { label: item.label, poles: item.poles })); };
  const tapSlot = (n) => {
    if (!armed) { setTap(n); return; }
    const r = placeLabel(docRef.current.schedule, derivedSpaces(docRef.current.schedule), armed, n);
    if (r.error) { setToast({ kind: "warn", text: r.error }); return; }
    change(r.next);
    setToast({ kind: "info", text: r.note || "Placed." });
    if (!/^(spare|space)$/i.test(armed.label)) setArmed(null); // Spare / Space stay armed for a run of slots
  };
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.kind === "warn" ? 8000 : 3200);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!armed) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setArmed(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [armed]);
  // opts.showPhoto = the "Show photo while reviewing" tick on the read summary.
  const applyPhoto = (mode, opts) => {
    const showPhoto = !opts || opts.showPhoto !== false;
    const job = intake.job;
    const sch = docRef.current.schedule;
    if (!job || !job.parsed || !sch) return;
    const ps = normaliseSchedule(job.parsed.schedule);
    if (mode === "replace" && circuitsOf(sch).length && !window.confirm(`Replace all ${circuitsOf(sch).length} circuits on this schedule with the ${ps.circuits.length} read from the photo?`)) return;
    const key = job.parsed.photoKey || job.photo?.key || null;
    const stamp = { ...(ps.parse?.photo || {}), kind: ps.parse?.photo?.kind || job.parsed.photoKind || null, model: ps.parse?.photo?.model || job.parsed.model || null, key };
    const { next, skipped } = applyParsed(sch, ps, mode, derivedSpaces(sch), stamp);
    change(next);
    // Ticked: pin the picture above the grid. Unticked: no pinned viewer (and it
    // does not come back by itself) — the row sheet still offers "Show the photo".
    setPinned(showPhoto ? { key, url: job.photo?.url || null, fileName: job.photo?.fileName || null } : null);
    setViewerClosed(!showPhoto);
    const amber = reviewRows(ps).length;
    setNote(`Applied from the photo${skipped ? ` — ${skipped} circuit${skipped === 1 ? "" : "s"} skipped because the slot was taken` : ""}.${amber ? ` ${amber} row${amber === 1 ? " is" : "s are"} amber: tap each one, check it against the photo, then Accept.` : ""}`);
    intake.clear();
  };

  // what = which button asked ("pdf" | "png" | "share") — it names the held prompt.
  const exportAs = (format, what, lastSaved) => afterSave(what, async () => {
    setExporting(format); setErr(null);
    const res = resolveSizeLocal(schedule, size);
    // A manual (unlocked) length rides along as custom_mm:{ w:62, h } on the
    // chosen preset; the server derives the pitch from it exactly as we do.
    const custom_mm = exportCustomMm(res, size) || undefined;
    const r = await api("export", { id: scheduleId, preset: res.preset, custom_mm, format });
    setExporting(null);
    if (!r.ok) { setErr(refusal(r, "Couldn't export")); return null; }
    setRow((p) => ({ ...(p || {}), rev: r.rev || p?.rev, export_preset: res.preset }));
    return r;
  }, lastSaved);
  // iOS Safari blocks window.open after an await, so the tab is opened blank
  // synchronously in the tap handler and pointed at the file once it exists.
  const openBlank = () => { try { return window.open("", "_blank"); } catch { return null; } };
  const landIn = (w, url) => {
    if (w && !w.closed) { w.location = url; return; }
    const w2 = openBlank();
    if (w2) w2.location = url; else setExportUrl(url); // popup blocked — leave a tap target
  };
  const closeBlank = (w) => { try { if (w && !w.closed) w.close(); } catch { /* cross-origin already */ } };
  const openExport = (format, lastSaved) => {
    const w = openBlank();
    setExportUrl(null);
    return exportAs(format, format, lastSaved).then((r) => {
      const url = r && (format === "png" ? r.pngUrl : r.pdfUrl);
      if (url) landIn(w, url); else closeBlank(w);
    }, (e) => { closeBlank(w); setErr(String(e?.message || e)); });
  };
  const share = (lastSaved) => {
    // With navigator.share the PNG goes to the share sheet, so no tab is
    // pre-opened (it would steal the screen); without it, pre-open as above.
    const canShareApi = typeof navigator !== "undefined" && typeof navigator.share === "function";
    const w = canShareApi ? null : openBlank();
    setExportUrl(null);
    return exportAs("png", "share", lastSaved).then(async (r) => {
      if (!r?.pngUrl) { closeBlank(w); return; }
      if (canShareApi) {
        try {
          const blob = await (await fetch(r.pngUrl)).blob();
          const file = new File([blob], `${(schedule.panel?.name || "panel").replace(/[^\w-]+/g, "_")}.png`, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: schedule.panel?.name || "Panel schedule" }); return; }
        } catch { /* user cancelled, or share unsupported — fall through to the URL */ }
      }
      landIn(w, r.pngUrl);
    }, (e) => { closeBlank(w); setErr(String(e?.message || e)); });
  };
  const push = (lastSaved) => afterSave("push", async () => {
    if (!window.confirm(`Send "${schedule.panel?.name}" to Jobber job #${row.job_number}? This posts a new note with the PDF and PNG attached.${lastSaved ? "\n\nThis sends the LAST SAVED copy — your latest edits have not reached the server and will not be in it." : ""}`)) return;
    setPushing(true); setPushResult(null); setErr(null);
    const r = await api("push", { id: scheduleId });
    setPushing(false);
    if (r.ok) { setPushResult(`Posted to Jobber — note ${r.noteId || "created"}${r.attached ? ` · ${r.attached} attachment${r.attached === 1 ? "" : "s"}` : ""}`); setRow((p) => ({ ...(p || {}), jobber_note_id: r.noteId, jobber_push_status: "ok" })); }
    else setPushResult(`Push failed — ${refusal(r, "unknown error")}`);
  }, lastSaved);
  // Re-run whatever a failed save stopped: again with a fresh save (Retry), or —
  // the tech's explicit choice — from the copy the server already has. Both are
  // called straight from a tap, so the export tab can still be opened.
  const runHeld = (what, lastSaved) => (what === "push" ? push(lastSaved) : what === "share" ? share(lastSaved) : what === "close" ? afterSave("close", onExit, lastSaved) : openExport(what, lastSaved));

  if (err && !schedule) return <Shell><Banner kind="err" text={err} /><div style={{ marginTop: 10 }}><Btn onClick={onExit}>Back</Btn></div></Shell>;
  if (!schedule || !row) return <Shell><div style={{ color: C.muted, padding: 20 }}>Loading the schedule…</div></Shell>;

  const spaces = derivedSpaces(schedule);
  const review = reviewRows(schedule);
  const unplacedAll = schedule.unplaced || [];
  const unplacedOpen = openUnplaced(schedule);
  // Review state, in parity with the server: once the document on screen IS the
  // copy the server handed back (get / save / accept), row.needs_review is the
  // answer and nothing stricter is re-derived from it here — an unplaced line
  // the server marked reviewed, or any rule it relaxes later, must not leave
  // export blocked on this side only. While the screen is ahead of the server
  // (an edit not saved yet) the same rule is applied locally as a preview; the
  // save response then settles it, and export / push re-check on the server.
  const inSync = schedule === synced;
  const blocked = inSync ? !!row.needs_review : needsReviewLocal(schedule);
  const blockReason = !blocked ? null
    : review.length ? `${review.length} row${review.length === 1 ? " still needs" : "s still need"} review — accept ${review.length === 1 ? "it" : "them"} first`
      : unplacedOpen.length ? `${unplacedOpen.length} unplaced line${unplacedOpen.length === 1 ? " still needs" : "s still need"} a look — Accept all, or clear ${unplacedOpen.length === 1 ? "it" : "them"}`
        : "This schedule is still marked for review — tap Accept all";
  const jobLinked = !!(row.jobber_job_id || row.jobber_request_id);
  // start/get/list carry row.company = { name, phone } (phone may be null — the
  // preview then shows the name only, never a placeholder).
  // Kept in `company` state: only start/get/list carry it, a save response doesn't.
  const co = company || row.company || null;
  const meta = { job: row.job_number, date: new Date().toISOString().slice(0, 10), company: co?.name || null, phone: co?.phone || null, logoUrl: co?.logoUrl || null };
  // The photo to check rows against: the one just applied, or — after a reload —
  // the one the schedule was read from, for as long as rows still need review.
  const listed = (key) => (key ? (intake.photos || []).find((p) => p.key === key && (p.url || p.thumbUrl)) : null);
  const fromList = pinned ? listed(pinned.key) : (!viewerClosed && review.length > 0 ? listed(schedule.parse?.photo?.key) : null);
  const viewPhoto = fromList ? { key: fromList.key, url: fromList.url || fromList.thumbUrl, fileName: fromList.fileName || null } : (pinned && pinned.url ? pinned : null);
  // The row sheet can show the picture even when the pinned viewer is off (it
  // then starts collapsed): the photo the schedule was read from, while rows need review.
  const readFrom = !viewPhoto && review.length > 0 ? listed(schedule.parse?.photo?.key) : null;
  const sheetPhoto = viewPhoto || (readFrom ? { key: readFrom.key, url: readFrom.url || readFrom.thumbUrl, fileName: readFrom.fileName || null } : null);
  // What a failed save stopped, in words (see afterSave). Offline with the edits
  // safe in the phone draft is the one case that may go ahead — from the last
  // saved copy, and only on the tech's say-so.
  const heldNoun = !held ? "" : held.what === "push" ? "note and card sent to Jobber" : held.what === "pdf" ? "PDF" : "PNG";
  const heldCanUseSaved = !!held && held.what !== "close" && held.offline && held.draft;
  const heldText = !held ? null
    : heldCanUseSaved ? `No connection — your latest edits are saved on this phone but have not reached the server. A ${heldNoun} made now will be of the last saved copy, without those edits.${row.needs_review ? " (That copy still has rows to review, so it can't go out either — try again when you have signal.)" : ""}`
      : `Couldn't save your latest edits (${held.text}) — ${held.what === "close" ? "the schedule is still open" : held.what === "push" ? "nothing was sent to Jobber" : "nothing was exported"}. ${held.draft ? "They are kept on this phone." : "They are only on this screen — this browser couldn't keep a copy, so don't close the page."}`;
  const heldActions = !held ? null : (
    <>
      <Btn kind="pri" onClick={() => runHeld(held.what, false)} style={{ padding: "7px 12px", fontSize: 12.5 }}>Retry</Btn>
      {heldCanUseSaved && !row.needs_review && <Btn onClick={() => runHeld(held.what, true)} style={{ padding: "7px 12px", fontSize: 12.5 }}>{held.what === "push" ? "Send the last saved copy" : `Make the ${heldNoun} from the last saved copy`}</Btn>}
      {held.what === "close" && <Btn kind="danger" onClick={() => runHeld("close", true)} style={{ padding: "7px 12px", fontSize: 12.5 }}>{held.draft ? "Close anyway" : "Close and lose the edits"}</Btn>}
    </>
  );
  const tapCircuit = tap ? circuitsOf(schedule).find((c) => slotsOf(c).includes(tap)) : null;
  const panel = schedule.panel;

  return (
    <Shell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <input value={panel.name || ""} onChange={(e) => setPanel({ name: e.target.value })} placeholder="Panel name" style={{ ...inputStyle, fontSize: 18, fontWeight: 800, background: "transparent", border: "none", padding: "2px 0", minHeight: 32 }} />
          <div style={{ fontSize: 11.5, color: C.faint }}>
            {panel.volts || "120/240"} {Number(panel.phases) === 3 ? "3Ø" : "1Ø"} · {spaces} spaces · {panel.main_amps ? `${panel.main_amps}A ${String(panel.main_type || "mb").toUpperCase()}` : "main ?"}{panel.brand ? ` · ${panel.brand}` : ""}{panel.location ? ` · @${panel.location}` : ""}
            {" · "}<button onClick={() => setShowHeader(!showHeader)} style={{ background: "none", border: "none", color: C.lime, cursor: "pointer", padding: 0, fontSize: 11.5 }}>{showHeader ? "hide details" : "edit details"}</button>
          </div>
        </div>
        <SaveChip stateName={saveState} rev={row.rev} />
      </div>

      {/* Job chip */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
        <div style={{ flex: 1, minWidth: 200, padding: "8px 10px", background: C.card, border: `1px solid ${jobLinked ? C.border : C.orange}`, borderRadius: 10, fontSize: 12.5 }}>
          {jobLinked ? (
            <>
              <b>{row.job_number ? `Job #${row.job_number}` : "Request"}</b>{row.client_name ? ` · ${row.client_name}` : ""}
              <div style={{ fontSize: 11, color: C.muted }}>{row.job_title || row.request_title || ""}{row.site_address ? ` · ${row.site_address}` : ""}</div>
            </>
          ) : <span style={{ color: C.orange, fontWeight: 700 }}>Not linked to a job — nothing can post to Jobber yet</span>}
        </div>
        <Btn onClick={() => setLinking(true)} style={{ padding: "8px 12px", fontSize: 12 }}>{jobLinked ? "Change job" : "Link job"}</Btn>
      </div>
      {linking && <JobLinker initialQ={row.client_name || ""} onClose={() => setLinking(false)} onPick={async (patch) => { setLinking(false); await patchRow(patch); }} />}

      {showHeader && (
        <Card title="Panel">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <Field label="Brand"><input value={panel.brand || ""} onChange={(e) => setPanel({ brand: e.target.value || null })} placeholder="Square D QO" style={inputStyle} /></Field>
            <Field label="Location"><input value={panel.location || ""} onChange={(e) => setPanel({ location: e.target.value || null })} placeholder="garage" style={inputStyle} /></Field>
            <Field label="Volts"><select value={panel.volts || "120/240"} onChange={(e) => setPanel({ volts: e.target.value, phases: /Y|600|208Y/.test(e.target.value) ? 3 : panel.phases })} style={inputStyle}>{VOLTS.map((v) => <option key={v}>{v}</option>)}</select></Field>
            <Field label="Phases"><select value={panel.phases || 1} onChange={(e) => setPanel({ phases: Number(e.target.value) })} style={inputStyle}><option value={1}>1φ</option><option value={3}>3φ</option></select></Field>
            <Field label="Spaces"><select value={panel.spaces || spaces} onChange={(e) => setPanel({ spaces: Number(e.target.value) })} style={inputStyle}>{SPACE_SIZES.map((s) => <option key={s} value={s}>{s} ({s * 2} ckt)</option>)}</select></Field>
            <Field label="Main amps"><input type="number" inputMode="numeric" value={panel.main_amps ?? ""} onChange={(e) => setPanel({ main_amps: e.target.value === "" ? null : Number(e.target.value) })} style={inputStyle} /></Field>
            <Field label="Main type"><select value={panel.main_type || "mb"} onChange={(e) => setPanel({ main_type: e.target.value })} style={inputStyle}><option value="mb">Main breaker</option><option value="mlo">Main lugs only</option></select></Field>
            <Field label="Bus amps"><input type="number" inputMode="numeric" value={panel.bus_amps ?? ""} onChange={(e) => setPanel({ bus_amps: e.target.value === "" ? null : Number(e.target.value) })} style={inputStyle} /></Field>
            <Field label="Notes" span><input value={panel.notes || ""} onChange={(e) => setPanel({ notes: e.target.value })} style={inputStyle} /></Field>
          </div>
        </Card>
      )}

      {err && <Banner kind="err" text={err} onClose={() => setErr(null)} />}
      {/* Stays until a save lands (or it is dismissed): nothing exports, sends or accepts past it. */}
      {saveErr && <Banner kind="err" text={`Couldn't save — ${saveErr}. Your edits are still on this screen.`} onClose={() => setSaveErr(null)} actions={<Btn kind="pri" onClick={() => flush()} style={{ padding: "7px 12px", fontSize: 12.5 }}>Retry</Btn>} />}
      {note && <Banner kind="info" text={note} onClose={() => setNote(null)} />}

      {/* Review strip — only what is still OPEN (an unplaced line the server marked reviewed no longer counts) */}
      {(review.length > 0 || unplacedOpen.length > 0 || blocked) && (
        <div style={{ marginTop: 10, padding: "9px 12px", background: "rgba(245,158,11,.10)", border: `1px solid ${C.orange}`, borderRadius: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, fontSize: 12.5, color: C.orange, fontWeight: 700 }}>
            {review.length ? `${review.length} row${review.length === 1 ? "" : "s"} to review (#${review.map((c) => c.number).join(", #")})` : ""}
            {review.length && unplacedOpen.length ? " · " : ""}
            {unplacedOpen.length ? `${unplacedOpen.length} unplaced line${unplacedOpen.length === 1 ? "" : "s"}` : ""}
            {!review.length && !unplacedOpen.length ? "Still marked for review" : ""}
          </div>
          {(review.length > 0 || blocked) && <Btn onClick={() => accept("all")} style={{ padding: "7px 11px", fontSize: 12 }}>Accept all</Btn>}
          {unplacedAll.length > 0 && <Btn onClick={() => change({ ...schedule, unplaced: [] })} style={{ padding: "7px 11px", fontSize: 12 }}>Clear unplaced</Btn>}
        </div>
      )}
      {unplacedAll.length > 0 && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
          {unplacedAll.map((u, i) => <div key={i}>• "{u?.raw}" — {u?.reason || "no circuit number"}{u?.reviewed ? " · reviewed" : ""}</div>)}
          {/* Only reviewed lines left: no amber strip, but they can still be removed. */}
          {!(review.length > 0 || unplacedOpen.length > 0 || blocked) && (
            <button onClick={() => change({ ...schedule, unplaced: [] })} style={{ background: "none", border: "none", color: C.lime, cursor: "pointer", padding: "6px 0", fontSize: 11.5, fontWeight: 700 }}>Remove these lines</button>
          )}
        </div>
      )}

      <LabelPalette org={row.organization_id} armed={armed} onArm={arm} onGrab={grabLabel} />

      <div ref={reviewRef}>
      {viewPhoto && <PhotoViewer photo={viewPhoto} containerRef={reviewRef} onReload={intake.refresh} onClose={() => { setPinned(null); setViewerClosed(true); }} />}
      <Card title={`Circuits (${circuitsOf(schedule).length} of ${spaces})`} right={
        <Btn kind="pri" style={{ padding: "6px 11px", fontSize: 12, minHeight: 32 }} onClick={() => {
          const at = lowestFree(schedule, spaces, 1);
          if (at) setTap(at);
          else { const bigger = SPACE_SIZES.find((s) => s > spaces); if (bigger) { setPanel({ spaces: bigger }); setNote(`Panel full — grew to ${bigger} spaces.`); } else setNote("Panel is full."); }
        }}>+ Add circuit</Btn>
      }>
        <Grid schedule={schedule} spaces={spaces} onTap={tapSlot} onGrab={grabCircuit} drag={slotDrag.drag} armed={!!armed} />
        <div style={{ fontSize: 11, color: C.faint, marginTop: 8, lineHeight: 1.5 }}>Tap a row to edit it. Drag ⠿ onto another slot to move a circuit, or onto a circuit with the same poles to swap them. Odd numbers left, even right; a 2-pole spans two rows on its side. Amber = needs a look.</div>
      </Card>
      </div>

      <SizePanel schedule={schedule} size={size} setSize={(sz) => change(null, sz)} meta={meta} onLogo={(logoUrl) => setCompany({ ...(co || {}), logoUrl })} />

      <PhotoCard intake={intake} jobLinked={jobLinked} canMerge={circuitsOf(schedule).length > 0} onApply={applyPhoto} />

      <PasteBox scheduleId={scheduleId} schedule={schedule} spaces={spaces} intake={intake} onApplyPhoto={applyPhoto} onApply={(next, skipped) => { change(next); setNote(skipped ? `Applied — ${skipped} circuit${skipped === 1 ? "" : "s"} skipped because the slot was taken.` : "Applied."); }} />

      <Card title="Print & send">
        {blocked && <div style={{ fontSize: 12, color: C.orange, marginBottom: 8 }}>{blockReason}.</div>}
        {!jobLinked && <div style={{ fontSize: 12, color: C.orange, marginBottom: 8 }}>Link a job to send to Jobber.</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn kind="pri" disabled={blocked || !!exporting} onClick={() => openExport("pdf")} title={blockReason || "Print-size PDF"}>{exporting === "pdf" ? "Building…" : "PDF"}</Btn>
          <Btn disabled={blocked || !!exporting} onClick={() => openExport("png")} title={blockReason || "PNG for the label app"}>{exporting === "png" ? "Building…" : "PNG"}</Btn>
          <Btn disabled={blocked || !!exporting} onClick={() => share()} title={blockReason || "Share the PNG to the printer app"}>Share</Btn>
          <div style={{ flex: 1 }} />
          <Btn disabled={blocked || !jobLinked || pushing} onClick={() => push()} title={blockReason || (!jobLinked ? "Link a job first" : "Post a note + card to the Jobber job")}>{pushing ? "Sending…" : "Send to Jobber"}</Btn>
        </div>
        {/* A save that didn't land stopped the button above: say so here, next to it. */}
        {held && held.what !== "close" && <Banner kind={heldCanUseSaved ? "warn" : "err"} text={heldText} onClose={() => setHeld(null)} actions={heldActions} />}
        {exportUrl && <div style={{ fontSize: 12.5, marginTop: 8 }}>The browser blocked the new tab — <a href={exportUrl} target="_blank" rel="noreferrer" style={{ color: C.lime, fontWeight: 700 }}>open the file</a>.</div>}
        {pushResult && <div style={{ fontSize: 12.5, color: pushResult.startsWith("Posted") ? C.green : C.red, marginTop: 8 }}>{pushResult}</div>}
        {row.jobber_note_id && !pushResult && <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8 }}>Last pushed {row.jobber_pushed_at ? new Date(row.jobber_pushed_at).toLocaleString() : ""} · note {row.jobber_note_id}</div>}
        <div style={{ fontSize: 11, color: C.faint, marginTop: 8, lineHeight: 1.5 }}>PDF pages are the exact media size — print at 100%, never "fit to page". Share sends the PNG to Brother iPrint&amp;Label on the phone.</div>
      </Card>

      {held && held.what === "close" && <Banner kind="err" text={heldText} onClose={() => setHeld(null)} actions={heldActions} />}
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <Btn onClick={() => afterSave("close", onExit)}>Save &amp; close</Btn>
        <Btn kind="danger" onClick={async () => { if (!window.confirm("Delete this schedule? This can't be undone.")) return; const r = await api("delete", { id: scheduleId }); if (r.ok) { try { localStorage.removeItem(LS_KEY(scheduleId)); } catch { /* ignore */ } onExit(); } else setErr(r.error || "Couldn't delete"); }}>Delete</Btn>
      </div>

      {tap && (
        <RowSheet
          key={`${tap}-${tapCircuit?.number ?? "new"}`}
          number={tapCircuit ? tapCircuit.number : tap}
          circuit={tapCircuit}
          schedule={schedule}
          spaces={spaces}
          photo={sheetPhoto}
          photoOpen={!!viewPhoto}
          onChange={(next) => change(next)}
          onAccept={accept}
          onClose={() => setTap(null)}
        />
      )}

      {/* Always on screen (the grid can be 40 rows away from the banners): which
          label is armed, and why a drop was refused. Sits above the phone's bottom nav. */}
      {(armed || toast) && !tap && !slotDrag.drag && (/* hidden mid-drag: it must never cover a slot the pointer is looking for */
        <div style={{ position: "fixed", left: 0, right: 0, bottom: "calc(var(--nav-h, 0px) + env(safe-area-inset-bottom, 0px) + 10px)", zIndex: 55, display: "flex", justifyContent: "center", padding: "0 12px", pointerEvents: "none" }}>
          <div style={{ pointerEvents: "auto", width: "100%", maxWidth: 560, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "2px 10px 10px", boxShadow: "0 10px 30px rgba(0,0,0,.6)" }}>
            {toast && <Banner kind={toast.kind} text={toast.text} onClose={() => setToast(null)} />}
            {armed && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.text, lineHeight: 1.4 }}>Placing <b style={{ color: C.lime }}>{armed.label}</b>{armed.poles === 2 ? " (2-pole)" : ""} — tap a slot.</span>
                <Btn onClick={() => setArmed(null)} style={{ padding: "6px 12px", fontSize: 12, minHeight: 36 }}>{/^(spare|space)$/i.test(armed.label) ? "Done" : "Cancel"}</Btn>
              </div>
            )}
          </div>
        </div>
      )}
    </Shell>
  );
}

// ── Index: list + search + New ──────────────────────────────────────────────
function Index({ onOpen }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const r = await api("list", { search: q.trim() || undefined, limit: 50 });
      if (!r.ok) { setErr(r.error || "Couldn't load schedules"); setRows([]); return; }
      setErr(null); setRows(r.schedules || []);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);
  const start = async () => {
    setStarting(true); setErr(null);
    const r = await api("start", {});
    setStarting(false);
    if (r.ok && r.schedule?.id) onOpen(r.schedule.id);
    else setErr(r.error || "Couldn't start a schedule");
  };
  return (
    <Shell>
      <h2 style={{ margin: "0 0 2px", fontSize: 19 }}>Panel schedules</h2>
      <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>Directory cards that print to fit the door · from a /panel note, a pasted list, or by hand</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search panel, client, job #…" style={inputStyle} />
        <Btn kind="pri" disabled={starting} onClick={start} style={{ whiteSpace: "nowrap" }}>{starting ? "Starting…" : "New schedule"}</Btn>
      </div>
      {err && <Banner kind="err" text={err} onClose={() => setErr(null)} />}
      <div style={{ display: "grid", gap: 8 }}>
        {rows === null ? <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>Loading…</div>
          : !rows.length ? <div style={{ color: C.muted, fontSize: 13, padding: 8, lineHeight: 1.5 }}>Nothing yet. Start one here, or add a note beginning with <code>/panel</code> on any Jobber job and it shows up on its own.</div>
          : rows.map((r) => (
            <button key={r.id} onClick={() => onOpen(r.id)} style={{ textAlign: "left", cursor: "pointer", padding: "10px 12px", background: C.card, border: `1px solid ${r.needs_review ? C.orange : C.border}`, borderRadius: 10, minHeight: 56 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.panel_name || "Panel"}{r.client_name ? ` · ${r.client_name}` : ""}</span>
                {r.needs_review && <span style={{ fontSize: 10, fontWeight: 800, color: C.orange, border: `1px solid ${C.orange}`, borderRadius: 999, padding: "2px 7px", whiteSpace: "nowrap" }}>NEEDS REVIEW</span>}
              </div>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>
                {r.job_number ? `job #${r.job_number} · ` : ""}{r.circuit_count ?? 0} circuits · rev {r.rev || 1}{r.source && r.source !== "designer" ? ` · from ${r.source}` : ""}{r.updated_at ? ` · ${new Date(r.updated_at).toLocaleDateString()}` : ""}
              </div>
            </button>
          ))}
      </div>
      <div style={{ marginTop: 16, fontSize: 11.5, color: C.faint, lineHeight: 1.6 }}>
        Cards print on a Brother QL 62 mm film strip or a letter sheet. <Link to="/schedule" style={{ color: C.lime }}>My Schedule</Link>
      </div>
    </Shell>
  );
}

// ── Route entry ─────────────────────────────────────────────────────────────
export default function PanelScheduleDesigner() {
  const { scheduleId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [autoErr, setAutoErr] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const tried = useRef(false);

  // Deep link from a visit or the schedule button: ?job=2509&visit=… → find-or-create.
  const job = search.get("job");
  const visit = search.get("visit");
  const request = search.get("request");
  const panelKey = search.get("panel");
  useEffect(() => {
    if (scheduleId || (!job && !request) || tried.current) return;
    tried.current = true;
    setAutoBusy(true);
    (async () => {
      const r = await api("start", { jobNumber: job || undefined, visitId: visit || undefined, requestId: request || undefined, panelKey: panelKey || undefined });
      setAutoBusy(false);
      if (r.ok && r.schedule?.id) navigate(`/panel-schedule/${r.schedule.id}`, { replace: true });
      else setAutoErr(r.error || "Couldn't open a schedule for that job");
    })();
  }, [scheduleId, job, visit, request, panelKey, navigate]);

  if (scheduleId) return <Designer key={scheduleId} scheduleId={scheduleId} onExit={() => navigate("/panel-schedule")} />;
  if (autoBusy) return <Shell><div style={{ color: C.muted, padding: 20 }}>Opening the schedule…</div></Shell>;
  if (autoErr) return <Shell><Banner kind="err" text={autoErr} /><div style={{ marginTop: 12 }}><Btn onClick={() => { setAutoErr(null); navigate("/panel-schedule", { replace: true }); }}>All schedules</Btn></div></Shell>;
  return <Index onOpen={(id) => navigate(`/panel-schedule/${id}`)} />;
}
